import type { UploadedDocResult } from '../store/useFormCGIAStore';
import type { CgDocType } from '../ocr/ocrResponseTypes';
import type { CrossCheckContext, CrossCheckFinding, CrossCheckRule } from './crossCheckEngine';
import { NUMERO_FORMULE_SIV } from './numeroFormule';
import {
  findSingleOcrConfusion,
  isProbableVinOcrVariant,
  getFieldConfidence,
  getFieldSource,
  getFieldValue,
  IMMAT_SIV_REGEX,
  isSingleOcrConfusion,
  monthsBetween,
  normalizeForCompare,
  parseDateMaybe,
  runCrossChecks,
  SIREN_REGEX,
  SKIPCAR_SIREN,
  SKIPCAR_SIRET,
  VIN_REGEX,
} from './crossCheckEngine';
import type { AddressDivergence } from './addressNormalize';
import { DIVERGENCE_LABELS, compareAddresses, isTranscriptionOnlyDivergence } from './addressNormalize';
import { isCtRequired, yearsSince } from './ctRequirement';
import { validateVinCheckDigit } from './vinChecksum';

/**
 * 18 règles de cross-validation pour FormCGIA.
 *
 * Dérivées du cahier des charges technique (§5 Règles de validation
 * automatique) ET du Manop CTVO SKIPCAR (blocages ANTS, cas particuliers).
 *
 * Chaque règle est pure et testable unitairement.
 */

const makeFinding = (
  ruleId: string,
  severity: 'error' | 'warning' | 'info',
  message: string,
  fields?: string[],
  documentTypes?: CgDocType[],
): CrossCheckFinding => ({ ruleId, severity, message, fields, documentTypes });

/**
 * Préfixe des findings « la pièce est là, mais nous n'avons pas su la vérifier ».
 *
 * Arbitrage François du 09/09/2026 (`piece_presente_dite_manquante`, 4 retours
 * derrière) : « une pièce déposée mais non vérifiable, on la considère présente
 * avec un badge non vérifiée » — jamais un blocage qui oblige l'agence à
 * redéposer une pièce déjà au dossier. Les retours visaient tous le même
 * scénario : un fichier porte les deux faces (permis recto/verso, CNI + permis
 * dans un même PDF), le pipeline n'en reconnaît qu'une, et le dossier se bloque
 * sur une face « manquante » qui est physiquement là — EM-686-XB, GP-130-FQ,
 * GB-391-ZT, GL-911-WF, FX-187-XA.
 *
 * Ne vaut QUE pour une pièce effectivement déposée. Une pièce absente du
 * dossier reste un blocage (cas 1 du référentiel CTVO §14) : c'est la ligne de
 * partage entre « non vérifiée » et « manquante ».
 */
const PIECE_NON_VERIFIEE = 'Pièce déposée, non vérifiée — ';

// 1. Format plaque d'immatriculation SIV
const regexImmatSiv: CrossCheckRule = {
  id: 'regex_immat_siv',
  description: "L'immatriculation doit respecter le format SIV (AA-123-BB)",
  severity: 'warning',
  check: (ctx) => {
    const immat = getFieldValue<string>(ctx.docs.carte_grise, 'immatriculation');
    if (!immat) return null;
    if (!IMMAT_SIV_REGEX.test(immat)) {
      return makeFinding(
        'regex_immat_siv',
        'warning',
        `L'immatriculation « ${immat} » ne suit pas le format SIV actuel (AA-123-BB). C'est sans doute un ancien numéro (FNI, type 123-AB-29). Pas bloquant : vérifiez juste d'un coup d'œil sur la carte grise.`,
        ['immatriculation'],
        ['carte_grise'],
      );
    }
    return null;
  },
};

// 2. Format VIN (17 caractères, pas de I/O/Q)
const vinFormat: CrossCheckRule = {
  id: 'vin_format',
  description: 'Le VIN doit faire 17 caractères sans I/O/Q',
  severity: 'error',
  check: (ctx) => {
    const vin = getFieldValue<string>(ctx.docs.carte_grise, 'vin');
    if (!vin) return null;
    if (!VIN_REGEX.test(vin)) {
      return makeFinding(
        'vin_format',
        'error',
        `Le VIN lu « ${vin} » n'est pas valide : un VIN fait toujours exactement 17 caractères et ne contient jamais les lettres I, O ni Q (trop proches de 1, 0 et Q). → Relisez-le sur la carte grise et corrigez-le, sinon l'ANTS rejettera le dossier.`,
        ['vin'],
        ['carte_grise'],
      );
    }
    return null;
  },
};

// 3. Cohérence immatriculation CG ↔ Cession. La carte grise (imprimée) fait
// foi face à la cession (souvent manuscrite) : quand l'écart se réduit à UN
// caractère OCR-confusable (J/L, O/0…), c'est très probablement une erreur
// de lecture, pas un vrai document divergent → mineure au lieu de majeure.
const coherenceImmatCgCession: CrossCheckRule = {
  id: 'coherence_immat_cg_cession',
  description: "L'immatriculation doit être identique sur la carte grise et le certificat de cession",
  severity: 'error',
  check: (ctx) => {
    const cgImmat = getFieldValue<string>(ctx.docs.carte_grise, 'immatriculation');
    const cessionImmat = getFieldValue<string>(ctx.docs.cerfa_cession, 'immatriculation');
    if (!cgImmat || !cessionImmat) return null;
    const cgNorm = normalizeForCompare(cgImmat);
    const cessionNorm = normalizeForCompare(cessionImmat);
    if (cgNorm === cessionNorm) return null;

    // Garde G.4 (DC-39460) : divergence détectée mais immatriculation
    // incertaine (< 0.92) → règle suspendue, l'info dédiée
    // coherence_immat_skipped_low_confidence prend le relais.
    if (isImmatConfidenceTooLow(ctx)) return null;

    if (isSingleOcrConfusion(cgNorm, cessionNorm)) {
      return makeFinding(
        'coherence_immat_cg_cession',
        'warning',
        `Petit écart d'immatriculation : carte grise « ${cgImmat} » vs cession « ${cessionImmat} » — un seul caractère diffère, typique d'une confusion de lecture (la cession est souvent manuscrite). La carte grise fait foi. Pas bloquant : un simple coup d'œil à l'original suffit à confirmer.`,
        ['immatriculation'],
        ['carte_grise', 'cerfa_cession'],
      );
    }

    return makeFinding(
      'coherence_immat_cg_cession',
      'error',
      `Les immatriculations ne correspondent pas : carte grise « ${cgImmat} » vs certificat de cession « ${cessionImmat} ». → Vérifiez qu'il s'agit bien du même véhicule et corrigez la pièce erronée (la carte grise fait foi), sinon l'ANTS rejettera le dossier.`,
      ['immatriculation'],
      ['carte_grise', 'cerfa_cession'],
    );
  },
};

// 4. Cohérence VIN CG ↔ Cession
// Sévérité warning (non bloquant) : le VIN est re-vérifié par l'agence dans
// l'écran récap. Cette règle ne sert qu'à remonter les VRAIS mismatches ;
// les différences OCR (O/0, I/1, etc.) sont détectées via le VIN_REGEX qui
// enforce les règles ISO 3779 (pas de I, O, Q, longueur 17). Si un VIN
// est invalide et l'autre valide, le valide est source de vérité et on
// ne pollue pas l'agence avec un warning.
const coherenceVinCgCession: CrossCheckRule = {
  id: 'coherence_vin_cg_cession',
  description: 'Le VIN doit être identique sur la carte grise et le certificat de cession',
  severity: 'warning',
  check: (ctx) => {
    const cgVin = getFieldValue<string>(ctx.docs.carte_grise, 'vin');
    const cessionVin = getFieldValue<string>(ctx.docs.cerfa_cession, 'vin');
    if (!cgVin || !cessionVin) return null;
    if (normalizeForCompare(cgVin) === normalizeForCompare(cessionVin)) return null;

    // Garde de confiance (DC-40245 : cession pivotée 90° mal redressée, VIN lu à
    // 55 %). Si l'une des deux lectures de VIN est incertaine (< 0.92, même seuil
    // que l'immat §13.5 et MiSIV), la divergence est un artefact de LECTURE, pas
    // une vraie incohérence documentaire — la carte grise (imprimée, source SIV)
    // fait foi. On rétrograde en INFO (note d'audit) au lieu de crier au loup,
    // d'autant que mandat / 13750 / CT confirment en général le VIN de la CG.
    if (isVinCgCessionConfidenceTooLow(ctx)) {
      return makeFinding(
        'coherence_vin_cg_cession',
        'info',
        `VIN divergent entre carte grise (${cgVin}) et cession (${cessionVin}), mais la lecture est incertaine (confiance OCR < 92 % — cession peu lisible, scan pivoté ou papier thermique). La carte grise (imprimée, source SIV) fait foi → VIN retenu : ${cgVin}. Vérification visuelle facultative sur l'original ; non bloquant.`,
        ['vin'],
        ['carte_grise', 'cerfa_cession'],
      );
    }

    const cgValid = VIN_REGEX.test(cgVin);
    const cessValid = VIN_REGEX.test(cessionVin);

    // Cas fréquent : exactement UN des deux VIN est invalide ISO 3779
    // (contient I/O/Q, ou longueur ≠ 17). Ce n'est pas une divergence
    // métier mais une simple erreur d'OCR sur le document invalide — la
    // version valide est source de vérité. Silencieux, pas de warning.
    if (cgValid !== cessValid) return null;

    // Les deux sont invalides : on remonte un warning spécifique car
    // l'extraction est douteuse des deux côtés (rare).
    if (!cgValid && !cessValid) {
      return makeFinding(
        'coherence_vin_cg_cession',
        'warning',
        `Le VIN semble mal lu sur les deux pièces (carte grise « ${cgVin} », cession « ${cessionVin} ») : aucun des deux n'est conforme — un VIN fait 17 caractères, sans I, O ni Q. → Relisez le VIN sur l'original et fiabilisez-le (la carte grise fait foi) avant l'envoi.`,
        ['vin'],
        ['carte_grise', 'cerfa_cession'],
      );
    }

    // Les deux VIN sont structurellement valides mais ne diffèrent que d'UN
    // caractère appartenant à une paire OCR-confusable connue (8/B, 0/O/D,
    // 1/I/L, 5/S, 2/Z…) : ce n'est pas une divergence documentaire mais un
    // artefact de LECTURE. Règle de confiance différenciée par source : la
    // carte grise — imprimée, générée par le SIV — fait foi face à la cession
    // (souvent manuscrite / scan de qualité moyenne). On rétrograde en INFO
    // (note d'audit indiquant la source retenue), sans demander de vérification
    // humaine à l'agence. Cas réel GK-008-GD / DC-39261 : « 8 » lu « B ».
    const vinConfusion = findSingleOcrConfusion(cgVin, cessionVin);
    if (vinConfusion) {
      return makeFinding(
        'coherence_vin_cg_cession',
        'info',
        `VIN identique à un caractère près entre la carte grise (${cgVin}) et la cession (${cessionVin}) : en position ${
          vinConfusion.index + 1
        }, « ${vinConfusion.to} » lu sur la cession au lieu de « ${vinConfusion.from} » — confusion OCR classique (${vinConfusion.from}/${vinConfusion.to}, indistinguables sur un scan). La carte grise (imprimée, source SIV) fait foi → VIN retenu : ${cgVin}. Non bloquant ; vérification visuelle facultative sur l'original.`,
        ['vin'],
        ['carte_grise', 'cerfa_cession'],
      );
    }

    // Écart limité à 1–2 éditions OCR (substitution confusable, lettre doublée
    // mal lue, ou trait fin parasite qui DÉCALE la lecture). Même logique de
    // confiance différenciée par source que ci-dessus, mais pour les divergences
    // multi-caractères dues à un décalage. Cas réel DC-39240 : cession lue
    // « W1AUZZ8X1CB032478 » (un « 1 » inséré) vs CG « WAUZZZ8X1CB032478 », alors
    // que mandat / CT / demande confirment tous la lecture de la carte grise.
    if (isProbableVinOcrVariant(cgVin, cessionVin)) {
      return makeFinding(
        'coherence_vin_cg_cession',
        'info',
        `VIN quasi identique entre la carte grise (${cgVin}) et la cession (${cessionVin}) : l'écart se limite à 1–2 caractères de confusion ou de décalage OCR (A/1, 0/O/D, 8/B, 5/S, 2/Z, 1/I/L, lettre doublée…). La carte grise (imprimée, source SIV) fait foi → VIN retenu : ${cgVin}. Non bloquant ; vérification visuelle facultative sur l'original.`,
        ['vin'],
        ['carte_grise', 'cerfa_cession'],
      );
    }

    // Sinon : les deux sont valides mais diffèrent sur PLUS d'un caractère (ou
    // sur des caractères non-confusables) → vrai mismatch de contenu à vérifier.
    return makeFinding(
      'coherence_vin_cg_cession',
      'warning',
      `Le VIN diffère entre la carte grise (${cgVin}) et la cession (${cessionVin}) — et les deux sont pourtant des VIN valides. → Comparez caractère par caractère sur l'original (la carte grise fait foi) et corrigez avant l'envoi pour éviter un rejet.`,
      ['vin'],
      ['carte_grise', 'cerfa_cession'],
    );
  },
};

// 5. Cohérence vendeur CG ↔ Cession (warning avec rattrapage décès / Parcours A)
const coherenceVendeurCgCession: CrossCheckRule = {
  id: 'coherence_vendeur_cg_cession',
  description: 'Le vendeur du certificat de cession doit être le titulaire de la carte grise',
  severity: 'warning',
  check: (ctx) => {
    const cgNom = getFieldValue<string>(ctx.docs.carte_grise, 'titulaire_nom');
    // Nom vendeur : raison sociale pro en priorité, sinon personne physique.
    const cessionVendeur = getCessionVendeurNom(ctx);
    if (!cgNom || !cessionVendeur) return null;

    // Le vendeur de la cession peut légitimement correspondre à PLUSIEURS
    // identités portées par la carte grise — sans aucun décès ni intermédiaire :
    //  - le NOM DE NAISSANCE du titulaire (C.1 ligne 1) ;
    //  - son NOM D'USAGE / nom marital (C.1 ligne 3, cf. §13.9 du référentiel) —
    //    ex EL-659-GT : titulaire « MORANGES épouse IANNASCOLI », cession signée
    //    « IANNASCOLI » ;
    //  - le CO-TITULAIRE (C.4.1, cf. §3-ter) — l'un des deux co-propriétaires vend.
    // On ne compare donc plus au seul nom de naissance : on accepte un match
    // contre l'une quelconque de ces identités. (Cas réel DC-39152 : faux
    // « Vente suite à un décès ? » alors que c'est un couple co-titulaire.)
    const cgNomUsage = getFieldValue<string>(ctx.docs.carte_grise, 'titulaire_nom_usage');
    const cgCotitulaireNom = getFieldValue<string>(ctx.docs.carte_grise, 'cotitulaire_nom');
    const cgOwnerNames = [cgNom, cgNomUsage, cgCotitulaireNom].filter((n): n is string => Boolean(n && n.trim()));
    const vendeurMatchesAnyOwner = cgOwnerNames.some(
      (owner) =>
        namesMatch(cessionVendeur, owner) || normalizeForCompare(owner) === normalizeForCompare(cessionVendeur),
    );

    // NOM ET PRÉNOM INVERSÉS sur la cession — CY-517-VW (retour du 22/07) :
    // carte grise « DREVON / BERNARD », cession « BERNARD / Drevon ». C'est la
    // même personne, remplie à l'envers dans les deux cases du Cerfa, et la
    // comparaison nom-à-nom y voyait une vente après décès. Le croisement doit
    // être complet des DEUX côtés pour conclure : un simple homonyme
    // (« MARTIN Bernard » vendu par « BERNARD Martin ») reste improbable, mais
    // il faudrait de toute façon deux inversions simultanées pour tromper la
    // garde.
    const cgTitulairePrenom = getFieldValue<string>(ctx.docs.carte_grise, 'titulaire_prenom');
    const cessionVendeurPrenomLu = getFieldValue<string>(ctx.docs.cerfa_cession, 'vendeur_prenom');
    const identiteInversee =
      Boolean(cgNom && cgTitulairePrenom && cessionVendeur && cessionVendeurPrenomLu) &&
      namesMatch(cessionVendeur, cgTitulairePrenom!) &&
      namesMatch(cessionVendeurPrenomLu!, cgNom);
    if (identiteInversee) {
      return makeFinding(
        'coherence_vendeur_cg_cession',
        'warning',
        `Le nom et le prénom du vendeur semblent inversés sur le certificat de cession : la carte grise porte ${cgNom} ${cgTitulairePrenom}, la cession ${cessionVendeur} ${cessionVendeurPrenomLu}. ` +
          "C'est la même personne, les deux cases du Cerfa sont remplies à l'envers. → Faites corriger la cession ; ce n'est ni une succession ni un vendeur différent.",
        ['seller_nom'],
        ['cerfa_cession'],
      );
    }
    if (vendeurMatchesAnyOwner) {
      // Même NOM de famille, PRÉNOM différent : la règle s'arrêtait là, et
      // DH-046-FJ (retour du 18/08) passait sans un mot — carte grise au nom de
      // GUENOLE Jacques, cession signée GUENOLE Ronan. C'est le cas typique de
      // la succession père/fils, que le nom de famille partagé masque
      // parfaitement. Alerte seule (arbitrage `nouveaux_controles_severite`) :
      // deux membres d'une famille peuvent aussi se revendre un véhicule, et
      // une lecture approximative d'un prénom ne doit pas coincer un dossier.
      const cgPrenom = getFieldValue<string>(ctx.docs.carte_grise, 'titulaire_prenom');
      const cgCotitulairePrenom = getFieldValue<string>(ctx.docs.carte_grise, 'cotitulaire_prenom');
      const cessionVendeurPrenom = getFieldValue<string>(ctx.docs.cerfa_cession, 'vendeur_prenom');
      const prenomsLus = Boolean(cgPrenom?.trim() && cessionVendeurPrenom?.trim());
      // Le vendeur peut être le CO-TITULAIRE : son prénom diffère alors
      // légitimement de celui du titulaire (EL-659-GT, couple co-titulaire).
      const cgOwnerPrenoms = [cgPrenom, cgCotitulairePrenom].filter((p): p is string => Boolean(p && p.trim()));
      const prenomsDivergents =
        prenomsLus && !cgOwnerPrenoms.some((prenom) => namesMatch(prenom, cessionVendeurPrenom!));
      // Une confusion OCR d'un seul caractère n'est pas un autre prénom.
      const divergenceReelle =
        prenomsDivergents &&
        cgOwnerPrenoms.every(
          (prenom) => !isSingleOcrConfusion(normalizeForCompare(prenom), normalizeForCompare(cessionVendeurPrenom!)),
        );

      if (divergenceReelle) {
        return makeFinding(
          'coherence_vendeur_cg_cession',
          'warning',
          `La carte grise est au nom de ${cgNom} ${cgPrenom}, mais la cession est signée par ${cessionVendeur} ${cessionVendeurPrenom} — même nom de famille, prénom différent. ` +
            'Ce cas se présente typiquement lors d\'une vente après décès, le véhicule étant vendu par un héritier. ' +
            "→ Si c'est une succession, joignez l'acte de décès et un justificatif d'héritier ; sinon, vérifiez les prénoms sur les deux pièces.",
          ['seller_nom'],
          ['carte_grise', 'cerfa_cession'],
        );
      }
      return null;
    }

    // Deux scénarios métier TRÈS différents derrière un vendeur ≠ titulaire :
    //  (a) vendeur PROFESSIONNEL ≠ titulaire particulier → intermédiaire qui
    //      revend (Parcours A). Ce n'est PAS un décès. parcours_a_requis /
    //      recepisse_da_manquant_parcours_a portent le suivi de la pièce.
    //  (b) vendeur PARTICULIER ≠ titulaire → succession/décès plausible,
    //      justificatif d'héritier à demander.
    if (isCessionVendeurPro(ctx)) {
      // Parcours A : si le récépissé de déclaration d'achat est DÉJÀ déposé, la
      // pièce requise est là — inutile de rappeler de la joindre (cas GK-008-GD
      // / DC-39261).
      if (ctx.docs.recepisse_da) return null;
      // FUSION (DC-39460) : « vendeur pro ≠ titulaire CG » et « récépissé DA
      // manquant » sont deux facettes du MÊME constat Parcours A. Quand le
      // titulaire C.1 est un particulier, la MAJEURE recepisse_da_manquant_
      // parcours_a porte déjà tout le constat — son message dit explicitement
      // « vendeur professionnel, C.1 carte grise particulier ». On s'efface
      // donc ici pour ne pas dédoubler l'information en un mineur + un majeur :
      // un seul bloquant, plus propre.
      const cgTitulaireSiren = getFieldValue<string>(ctx.docs.carte_grise, 'titulaire_siren');
      const cgTitulaireParticulier = !cgTitulaireSiren || !cgTitulaireSiren.trim();
      if (cgTitulaireParticulier) return null;
      // Cas résiduel pro→pro (titulaire C.1 lui-même professionnel) : la MAJEURE
      // Parcours A ne se déclenche pas (elle exige un C.1 particulier). On
      // conserve alors ce point d'attention unique pour ne pas perdre le signal.
      return makeFinding(
        'coherence_vendeur_cg_cession',
        'warning',
        `Le vendeur de la cession (${cessionVendeur}) est un professionnel, distinct du titulaire de la carte grise (${cgNom}) : c'est une revente par un intermédiaire (Parcours A), pas une vente entre particuliers ni un décès. → Assurez-vous que le récépissé de déclaration d'achat du professionnel est bien joint au dossier.`,
        ['seller_nom'],
        ['carte_grise', 'cerfa_cession'],
      );
    }
    return makeFinding(
      'coherence_vendeur_cg_cession',
      'warning',
      `Le vendeur de la cession (${cessionVendeur}) n'est pas le titulaire de la carte grise (${cgNom}) — ni son nom d'usage, ni un co-titulaire. Ce cas se présente typiquement lors d'une vente après décès. → Si c'est une succession, joignez un justificatif d'héritier (acte de notoriété) ; sinon, vérifiez simplement les noms sur les deux pièces.`,
      ['seller_nom'],
      ['carte_grise', 'cerfa_cession'],
    );
  },
};

// 6. Cohérence acquéreur Cession ↔ PI (blocage ANTS n°2)
//
// Subtilités métier (cumulées) :
//  - la cession ne reprend souvent qu'UN prénom alors que la PI en liste
//    plusieurs (ex : cession = "Luca", PI = "Luca, Yan, Daivy")
//  - idem pour le NOM : les Français utilisent fréquemment un nom composé
//    d'usage (accolé) sur la cession et seulement le nom de naissance (ou
//    inverse) sur la PI. Ex : cession = "MANCOIS DUCROIX", PI = "MANCOIS".
//
// → On applique la même règle d'inclusion de tokens que pour les prénoms :
//   chaque token de la valeur la plus courte doit apparaître dans la plus
//   longue. Ça préserve les vrais mismatches ("DUPONT" vs "MARTIN") tout
//   en tolérant les compositions.
const splitNameTokens = (raw: string): string[] =>
  raw
    .split(/[,;/&+\-']/)
    .flatMap((p) => p.split(/\s+/))
    .map(normalizeForCompare)
    .filter((p) => p.length > 0);

const tokensInclude = (shorter: string[], longer: string[]): boolean => {
  if (shorter.length === 0) return true;
  const set = new Set(longer);
  return shorter.every((t) => set.has(t));
};

/**
 * Inclusion tolérante : deux noms matchent si l'un est inclus dans l'autre
 * (token à token). Couvre les cas nom marital / nom d'usage / composition.
 */
const namesMatch = (a: string, b: string): boolean => {
  const tA = splitNameTokens(a);
  const tB = splitNameTokens(b);
  if (tA.length === 0 || tB.length === 0) return false;
  return tokensInclude(tA, tB) || tokensInclude(tB, tA);
};

/**
 * Le nom de référence (cession/mandat) peut désigner la personne par son nom de
 * naissance OU son nom d'usage — la pièce d'identité porte les deux. FX-187-XA :
 * cession « Véronique ROGER » vs CNI « MOUSSIER » nom d'usage « ROGER » → sans
 * le nom d'usage, faux blocage « ne correspond à aucune pièce d'identité ».
 */
const nomMatchesPi = (refNom: string, pi: UploadedDocResult | undefined): boolean => {
  const piNom = getFieldValue<string>(pi, 'nom');
  const piNomUsage = getFieldValue<string>(pi, 'nom_usage');
  return Boolean((piNom && namesMatch(refNom, piNom)) || (piNomUsage && namesMatch(refNom, piNomUsage)));
};

/**
 * Nom du vendeur sur la cession (raison sociale pro en priorité, sinon nom
 * personne physique). Sert à savoir si le cédant est le titulaire de la CG.
 */
const getCessionVendeurNom = (ctx: CrossCheckContext): string | null =>
  getFieldValue<string>(ctx.docs.cerfa_cession, 'vendeur_raison_sociale') ??
  getFieldValue<string>(ctx.docs.cerfa_cession, 'vendeur_nom');

/** true si le cédant de la cession est un professionnel (personne morale OU SIRET présent). */
const isCessionVendeurPro = (ctx: CrossCheckContext): boolean => {
  const estPM = getFieldValue<boolean>(ctx.docs.cerfa_cession, 'vendeur_est_personne_morale') === true;
  const siret = getFieldValue<string>(ctx.docs.cerfa_cession, 'vendeur_siret');
  return estPM || Boolean(siret && siret.trim());
};

/**
 * true si le cédant de la cession EST le titulaire de la carte grise (même
 * entité). Dans ce cas, c'est une VENTE DIRECTE — jamais un Parcours A
 * (intermédiaire professionnel), même quand le cédant est un pro qui revend
 * son propre véhicule. Cas DC-39988 : AGVV (pro) titulaire C.1 === AGVV
 * vendeur cession → vente directe, AUCUN récépissé de déclaration d'achat
 * exigible.
 */
const isVendeurSameAsCgTitulaire = (ctx: CrossCheckContext): boolean => {
  const vendeur = getCessionVendeurNom(ctx);
  const titulaire = getFieldValue<string>(ctx.docs.carte_grise, 'titulaire_nom');
  return Boolean(vendeur && titulaire && namesMatch(vendeur, titulaire));
};

/**
 * true si la pièce fournie concerne MANIFESTEMENT un autre véhicule que celui
 * du dossier : son immatriculation diffère de la carte grise par bien plus
 * qu'un caractère OCR-confusable, ET (quand les deux VIN sont lisibles) son
 * VIN diffère aussi de celui de la CG. Signal d'un fichier d'un AUTRE dossier
 * glissé par erreur dans le ZIP (cas DC-40000 : mandat BMW EH-668-NM dans un
 * dossier Opel GN-079-RQ) — à distinguer d'une simple erreur de lecture.
 */
const isPieceForAnotherVehicle = (ctx: CrossCheckContext, doc: UploadedDocResult | undefined): boolean => {
  if (!doc) return false;
  const cgImmat = getFieldValue<string>(ctx.docs.carte_grise, 'immatriculation');
  const docImmat = getFieldValue<string>(doc, 'immatriculation');
  if (!cgImmat || !docImmat) return false;
  // Garde confiance (§13.5 — même seuil que isImmatConfidenceTooLow) : on ne
  // déclare JAMAIS « autre véhicule » sur une immatriculation lue avec une
  // confiance insuffisante. Une plaque mal lue n'est PAS un autre véhicule,
  // c'est un misread — coherence_immat_skipped_low_confidence porte déjà l'INFO.
  // Cas FH-451-ZR : un Cerfa 13750 mal classé en cession, immat lue « EH-451-RB »
  // à 0.72 (F→E, Z→R, R→B) + VIN à 0.60 → faux « autre véhicule ».
  const docImmatConf = getFieldConfidence(doc, 'immatriculation');
  if (docImmatConf !== null && docImmatConf < IMMAT_CONFIDENCE_THRESHOLD) return false;
  const cgImmatN = normalizeForCompare(cgImmat);
  const docImmatN = normalizeForCompare(docImmat);
  if (cgImmatN === docImmatN) return false;
  // Écart d'un seul caractère confusable → erreur de lecture, PAS un autre
  // véhicule : on laisse coherence_immat_user_vs_pieces gérer en mineur.
  if (isSingleOcrConfusion(docImmatN, cgImmatN)) return false;

  // Immatriculation franchement différente. Si les deux VIN sont lisibles,
  // on confirme avec le VIN (le plus discriminant). Si le VIN du doc manque,
  // l'immatriculation totalement différente suffit déjà à conclure.
  const cgVin = getFieldValue<string>(ctx.docs.carte_grise, 'vin');
  const docVin = getFieldValue<string>(doc, 'vin');
  if (cgVin && docVin) {
    const vinDiffere = normalizeForCompare(cgVin) !== normalizeForCompare(docVin);
    return vinDiffere;
  }
  return true;
};

interface PickedAcquereurPi {
  pi: UploadedDocResult;
  slot: 'piece_identite' | 'piece_identite_hebergeur';
  swapped: boolean;
}

/**
 * P3.4 — Sélectionne la CNI de l'acquéreur parmi les pièces d'identité du
 * dossier. Méthodologie François (ctvo_system_v1.md §2.3, cas DC-39685) :
 * en mode hébergement, deux CNI sont attendues — l'acquéreur est identifié
 * par CROISEMENT nom cession, pas par position de dépôt.
 *
 * Stratégie :
 *  - 0 CNI         → null (la pièce manquante est remontée par une autre règle)
 *  - 1 CNI         → la retourne telle quelle, swapped=false
 *  - 2 CNI         → la CNI dont le nom matche la cession EST l'acquéreur,
 *                    même si l'auto-extract l'avait rangée en slot hébergeur
 *                    (swapped=true). Si aucune ne matche, on retourne la
 *                    `piece_identite` principale et c'est l'appelant qui décide
 *                    du verdict (incohérence vs mode hébergement à éclaircir).
 */
const pickAcquereurPi = (ctx: CrossCheckContext): PickedAcquereurPi | null => {
  const piMain = ctx.docs.piece_identite;
  const piHebergeur = ctx.docs.piece_identite_hebergeur;

  if (!piMain && !piHebergeur) return null;
  if (piMain && !piHebergeur) {
    return { pi: piMain, slot: 'piece_identite', swapped: false };
  }
  if (!piMain && piHebergeur) {
    return { pi: piHebergeur, slot: 'piece_identite_hebergeur', swapped: true };
  }

  // 2 CNI : routage par croisement nom + PRÉNOM avec la cession.
  // En hébergement INTRAFAMILIAL (un parent héberge son enfant), les deux CNI
  // partagent le NOM DE FAMILLE → le nom ne discrimine pas, seul le PRÉNOM
  // identifie l'acquéreur. On score donc chaque CNI (prénom prioritaire sur le
  // nom) et on retient la mieux corrélée à l'acquéreur de la cession.
  // Cas DC-39257 : CNI Paola (hébergeur) + CNI Claudia (acquéreuse), même nom
  // « SOUSA LOBO » → c'est le prénom « Claudia » de la cession qui tranche, sans
  // quoi la CNI de l'hébergeur passe à tort pour celle de l'acquéreur.
  const cessionNom = getFieldValue<string>(ctx.docs.cerfa_cession, 'acheteur_nom');
  const cessionPrenom = getFieldValue<string>(ctx.docs.cerfa_cession, 'acheteur_prenom');
  if (!cessionNom && !cessionPrenom) {
    // Pas de cession lisible — on ne peut pas router, on retourne la principale.
    return { pi: piMain!, slot: 'piece_identite', swapped: false };
  }
  const scorePi = (pi: UploadedDocResult | undefined): number => {
    const prenom = getFieldValue<string>(pi, 'prenom');
    const nomOk = Boolean(cessionNom && nomMatchesPi(cessionNom, pi));
    const prenomOk = Boolean(prenom && cessionPrenom && namesMatch(cessionPrenom, prenom));
    // Le prénom pèse plus que le nom : en hébergement intrafamilial, c'est lui
    // qui sépare l'acquéreur de l'hébergeur (nom de famille commun).
    return (prenomOk ? 2 : 0) + (nomOk ? 1 : 0);
  };
  const mainScore = scorePi(piMain);
  const hebergeurScore = scorePi(piHebergeur);

  // L'acquéreur est la CNI la mieux corrélée à la cession ; à égalité, on garde
  // le slot principal (comportement historique).
  if (hebergeurScore > mainScore) {
    // L'auto-extract avait rangé l'acquéreur en hébergeur → swap implicite.
    return { pi: piHebergeur!, slot: 'piece_identite_hebergeur', swapped: true };
  }
  return { pi: piMain!, slot: 'piece_identite', swapped: false };
};

/**
 * L'identité de l'acquéreur est établie dès que SON PERMIS DE CONDUIRE (pièce
 * maîtresse CTVO, toujours fournie par le nouveau titulaire) corrobore nom ET
 * prénom de la cession — le permis couvre l'identité acquéreur (skill §1, §13.13).
 *
 * En hébergement INTRAFAMILIAL (un parent héberge son enfant majeur), le slot
 * `piece_identite` peut légitimement être occupé par la CNI de l'HÉBERGEUR (même
 * nom de famille), tandis que l'acquéreur n'a fourni, côté pièce d'identité, que
 * le CNI « fantôme » d'un fichier multi-doc permis+CNI qui a ensuite été écrasé
 * dans le slot. Son identité reste prouvée par son permis → ne pas réclamer « sa
 * CNI » (faux positif AA-116-WS / DC-39685 : permis Tilio + CNI Guillaume hébergeur).
 */
/**
 * Le permis déposé vaut-il pièce d'identité ?
 *
 * Arbitrage François du 09/09/2026 (`permis_piece_identite`) : « le format carte
 * bancaire vaut pièce d'identité pour tout le monde. Le format permis papier
 * rose ne vaut pas pièce d'identité, mais reste valide comme permis de conduire
 * du titulaire d'une personne physique. »
 *
 * Discriminant : le format carte (post-2013) porte une date d'expiration (4b),
 * le rose pré-2013 n'en a aucune et reste valable à vie (référentiel CTVO §6).
 *
 * ⚠ On ne DÉDUIT pas l'ancien format du silence de l'OCR : une date
 * d'expiration non lue sur un permis carte est une lecture ratée, pas un permis
 * rose (règle maîtresse du référentiel — « donnée mal lue ≠ donnée manquante »).
 * Le rose n'est donc écarté que lorsqu'il est POSITIVEMENT identifié, par une
 * délivrance antérieure au 19/01/2013, date de bascule du format européen.
 */
const PERMIS_FORMAT_CARTE_DEPUIS = new Date('2013-01-19T00:00:00Z');

const permisVautPieceIdentite = (permis: UploadedDocResult | undefined): boolean => {
  if (!permis) return false;
  if (getFieldValue<string>(permis, 'date_expiration')) return true;

  const delivrance = getFieldValue<string>(permis, 'date_delivrance');
  const delivranceDate = delivrance ? new Date(delivrance) : null;
  const estAncienFormatRose =
    delivranceDate !== null && !Number.isNaN(delivranceDate.getTime()) && delivranceDate < PERMIS_FORMAT_CARTE_DEPUIS;

  return !estAncienFormatRose;
};

const permisIdentifieAcquereur = (
  ctx: CrossCheckContext,
  cessionNom: string,
  cessionPrenom: string | null | undefined,
): boolean => {
  if (!cessionPrenom) return false;
  // Un permis rose ne remplace pas la pièce d'identité (arbitrage ci-dessus) :
  // sans cette garde, un dossier sans CNI passait sur le seul permis, quel que
  // soit son format.
  if (!permisVautPieceIdentite(ctx.docs.permis_conduire)) return false;
  const permisNom = getFieldValue<string>(ctx.docs.permis_conduire, 'nom');
  const permisPrenom = getFieldValue<string>(ctx.docs.permis_conduire, 'prenom');
  if (!permisNom || !permisPrenom) return false;
  return nomMatchesPi(cessionNom, ctx.docs.permis_conduire) && namesMatch(cessionPrenom, permisPrenom);
};

const coherenceAcquereurCessionVsPi: CrossCheckRule = {
  id: 'coherence_acquereur_cession_vs_pi',
  description: "Le nom de l'acquéreur sur la cession doit correspondre à la pièce d'identité",
  severity: 'error',
  check: (ctx) => {
    const cessionNom = getFieldValue<string>(ctx.docs.cerfa_cession, 'acheteur_nom');
    const cessionPrenom = getFieldValue<string>(ctx.docs.cerfa_cession, 'acheteur_prenom');
    if (!cessionNom) return null;

    // AA-116-WS / DC-39685 — l'identité de l'acquéreur est déjà établie par SON
    // PERMIS (qui corrobore nom+prénom de la cession). Un slot `piece_identite`
    // occupé par la CNI de l'hébergeur (hébergement intrafamilial, même nom de
    // famille) ne doit alors PAS déclencher « fournir la CNI de l'acquéreur » :
    // la pièce d'identité de l'acquéreur existe (son permis), le décalage n'est
    // qu'un artefact d'attribution de slot.
    if (permisIdentifieAcquereur(ctx, cessionNom, cessionPrenom)) return null;

    // P3.4 — Routage automatique acquéreur/hébergeur par croisement cession,
    // au lieu de la mitigation Lot 3 #3 qui rétrogradait l'alerte. Cas
    // DC-39685 : si l'auto-extract a rangé l'acquéreur en slot hébergeur (et
    // inversement), le helper swap implicitement, et la cohérence d'identité
    // est validée avec la BONNE CNI.
    const picked = pickAcquereurPi(ctx);
    if (!picked) return null;

    const piNom = getFieldValue<string>(picked.pi, 'nom');
    const piPrenom = getFieldValue<string>(picked.pi, 'prenom');
    if (!piNom) return null;

    if (nomMatchesPi(cessionNom, picked.pi)) {
      // Routage OK sur le nom de famille. Vérifier le prénom aussi.
      if (cessionPrenom && piPrenom && !namesMatch(cessionPrenom, piPrenom)) {
        // PATTERN HÉBERGEMENT INTRAFAMILIAL (AA-116-WS / DC-39685) :
        // Guillaume ROBEQUAIN héberge son fils Tilio ROBEQUAIN. L'agence
        // a déposé la CNI de l'hébergeur (Guillaume) au lieu de celle de
        // l'acquéreur (Tilio). Mêmes nom de famille (ROBEQUAIN) mais
        // prénoms différents → ce n'est PAS une erreur d'orthographe,
        // c'est la structure même de l'hébergement.
        //
        // Si on retrouve les 2 prénoms cession/CNI dans les rôles
        // hébergé/hébergeur de l'attestation, on émet un message qui
        // pointe vers la vraie action (fournir la CNI de l'acquéreur)
        // plutôt qu'envoyer l'agence chasser une faute d'orthographe.
        const attestation = ctx.docs.attestation_hebergement;
        if (attestation) {
          const hebergeurPrenom = getFieldValue<string>(attestation, 'hebergeur_prenom');
          const hebergePrenom = getFieldValue<string>(attestation, 'heberge_prenom');
          const ciMatchesHebergeur = hebergeurPrenom && namesMatch(hebergeurPrenom, piPrenom);
          const cessionMatchesHeberge = hebergePrenom && namesMatch(hebergePrenom, cessionPrenom);
          if (ciMatchesHebergeur && cessionMatchesHeberge) {
            // Garde MULTI-DOC : la CNI de l'acquéreur est physiquement
            // dans le dossier si le slot piece_identite est rempli, même
            // via un fichier multi-pages permis+CNI où le pipeline a
            // marqué piece_identite comme "reçue" sans ré-extraire ses
            // champs (warning OCR "contient_aussi_piece_identite",
            // PiecesGrid clone le doc avec docTypeDetected swappé).
            // Dans ce cas, pickAcquereurPi a swap sur piHebergeur uniquement
            // parce que piMain.nom est null — mais la CNI ANTS est bien là.
            // Pas d'alerte : l'agence n'a rien à redéposer.
            if (ctx.docs.piece_identite && picked.slot === 'piece_identite_hebergeur') {
              return null;
            }
            return makeFinding(
              'coherence_acquereur_cession_vs_pi',
              'warning',
              `Hébergement intrafamilial détecté : ${hebergeurPrenom} ${cessionNom} (hébergeur) a déposé sa CNI, mais l'acquéreur du véhicule est ${hebergePrenom} ${cessionNom} (hébergé). Pour valider auprès de l'ANTS, fournir aussi la CNI de ${hebergePrenom} ${cessionNom}.`,
              ['buyer_prenom'],
              ['cerfa_cession', picked.slot, 'attestation_hebergement'],
            );
          }
        }
        return makeFinding(
          'coherence_acquereur_cession_vs_pi',
          'error',
          `Le nom de famille concorde (${cessionNom}) mais le prénom diffère entre la cession (${cessionPrenom}) et la pièce d'identité (${piPrenom}). → Vérifiez l'orthographe (accents, prénoms composés) et alignez la cession sur la CNI : un prénom non identique est la cause n°1 de rejet ANTS.`,
          ['buyer_prenom'],
          ['cerfa_cession', picked.slot],
        );
      }
      return null; // tout aligné
    }

    // La CNI sélectionnée NE matche PAS la cession.
    const hasHebergement = Boolean(ctx.docs.attestation_hebergement);
    const piHebergeur = ctx.docs.piece_identite_hebergeur;
    const onlyOnePi = !piHebergeur;

    if (hasHebergement && onlyOnePi) {
      // Mode hébergement annoncé mais une SEULE CNI déposée — probablement
      // celle de l'hébergeur. La CNI de l'acquéreur manque.
      return makeFinding(
        'coherence_acquereur_cession_vs_pi',
        'warning',
        `Dossier hébergement détecté (attestation présente) : la seule CNI fournie (${piPrenom ?? ''} ${piNom}) ne correspond pas à l'acquéreur de la cession (${cessionPrenom ?? ''} ${cessionNom}). C'est probablement la CNI de l'hébergeur. Fournir la CNI de l'acquéreur (qui sera rangée automatiquement en slot acquéreur).`,
        ['buyer_nom', 'buyer_prenom'],
        ['cerfa_cession', 'piece_identite', 'attestation_hebergement'],
      );
    }

    // Vraie incohérence d'identité (pas un cas hébergement résolvable).
    return makeFinding(
      'coherence_acquereur_cession_vs_pi',
      'error',
      `L'acquéreur sur le certificat de cession (${cessionPrenom ?? ''} ${cessionNom}) ne correspond à aucune pièce d'identité du dossier. Vérifiez les accents, tirets et orthographe — c'est la cause n°1 de blocage ANTS.`,
      ['buyer_nom', 'buyer_prenom'],
      ['cerfa_cession', 'piece_identite'],
    );
  },
};

// 7. CT obligatoire si MEC > 4 ans. Le champ X.1 de la carte grise ne dispense
// JAMAIS du PV (arbitrage François 2026-09-24, cf. ctRequirement.ts).
const ctObligatoireSiMec4Ans: CrossCheckRule = {
  id: 'ct_obligatoire_si_mec_4_ans',
  description: 'Un contrôle technique est obligatoire si le véhicule a plus de 4 ans',
  severity: 'error',
  check: (ctx) => {
    const mecRaw = getFieldValue<string>(ctx.docs.carte_grise, 'date_premiere_mec');
    const mec = parseDateMaybe(mecRaw);
    if (!mec) return null;
    if (ctx.docs.controle_technique) return null;

    if (!isCtRequired({ dateMecRaw: mecRaw, now: ctx.now })) return null;

    const ageYears = yearsSince(mec, ctx.now);
    return makeFinding(
      'ct_obligatoire_si_mec_4_ans',
      'error',
      `Le véhicule a ${ageYears.toFixed(1)} ans (plus de 4 ans) : un contrôle technique de moins de 6 mois est obligatoire, or il manque au dossier. → Ajoutez le procès-verbal de CT, sinon l'ANTS rejettera la demande.`,
      undefined,
      ['controle_technique'],
    );
  },
};

// 8. CT toujours valide (< 6 mois favorable / < 2 mois non-favorable)
const ctValidite: CrossCheckRule = {
  id: 'ct_validite',
  description: 'Le contrôle technique doit être valide (favorable < 6 mois, non-favorable < 2 mois)',
  severity: 'error',
  check: (ctx) => {
    const ctDoc = ctx.docs.controle_technique;
    if (!ctDoc) return null;
    const dateCtRaw = getFieldValue<string>(ctDoc, 'date_ct');
    const resultat = getFieldValue<string>(ctDoc, 'resultat');
    const dateCt = parseDateMaybe(dateCtRaw);
    if (!dateCt || !resultat) return null;
    const months = monthsBetween(dateCt, ctx.now);
    const isFavorable = resultat === 'favorable';
    const threshold = isFavorable ? 6 : 2;
    if (months > threshold) {
      return makeFinding(
        'ct_validite',
        'error',
        `Le contrôle technique date de ${months.toFixed(1)} mois, au-delà de la limite de ${threshold} mois pour un résultat « ${resultat} ». → Fournissez un CT plus récent (ou la contre-visite favorable), sinon l'ANTS le refusera.`,
        undefined,
        ['controle_technique'],
      );
    }
    return null;
  },
};

// 9. L'assurance ne doit pas être un devis
const assuranceNonDevis: CrossCheckRule = {
  id: 'assurance_non_devis',
  description: "L'attestation d'assurance ne doit pas être un devis",
  severity: 'error',
  check: (ctx) => {
    const estDevis = getFieldValue<boolean>(ctx.docs.assurance, 'est_devis');
    if (estDevis === true) {
      return makeFinding(
        'assurance_non_devis',
        'error',
        "Le document d'assurance fourni est un devis, pas une couverture effective. → Fournissez l'attestation ou le mémo d'assurance définitif : un devis est systématiquement refusé par l'ANTS.",
        undefined,
        ['assurance'],
      );
    }
    return null;
  },
};

// 10. L'assurance doit matcher immat OU VIN du véhicule
const assuranceMatchVehicule: CrossCheckRule = {
  id: 'assurance_match_vehicule',
  description: "L'assurance doit mentionner l'immatriculation ou le VIN du véhicule",
  severity: 'error',
  check: (ctx) => {
    const assuranceDoc = ctx.docs.assurance;
    if (!assuranceDoc) return null;
    const assuranceImmat = getFieldValue<string>(assuranceDoc, 'immatriculation');
    const assuranceVin = getFieldValue<string>(assuranceDoc, 'vin');
    const cgImmat = getFieldValue<string>(ctx.docs.carte_grise, 'immatriculation');
    const cgVin = getFieldValue<string>(ctx.docs.carte_grise, 'vin');
    if (!cgImmat && !cgVin) return null;
    const immatMatch = assuranceImmat && normalizeForCompare(assuranceImmat) === normalizeForCompare(cgImmat);
    const vinMatch = assuranceVin && normalizeForCompare(assuranceVin) === normalizeForCompare(cgVin);
    if (!immatMatch && !vinMatch) {
      return makeFinding(
        'assurance_match_vehicule',
        'error',
        "L'attestation d'assurance ne mentionne ni l'immatriculation ni le VIN du véhicule du dossier : impossible de prouver qu'elle couvre bien CE véhicule. → Fournissez une attestation portant la plaque ou le VIN, sinon l'ANTS la refusera.",
        undefined,
        ['assurance'],
      );
    }
    return null;
  },
};

// 11. Justificatif de domicile de moins de 6 mois
/**
 * Types de justif_domicile soumis à la règle ANTS « avis d'imposition de
 * l'année précédente » → restent valides toute l'année courante, peu
 * importe la date d'émission. Pas de fenêtre "< 6 mois" pour ceux-ci.
 *
 * Référence : règle Skipcar communiquée à l'agence :
 *   « Avis d'imposition ou de non-imposition de l'année précédente
 *     (impôt sur le revenu, taxe d'habitation ou taxes foncières) »
 */
const FISCAL_JDC_TYPES = new Set(['avis_imposition', 'taxe_fonciere']);

const justifDomicileRecent: CrossCheckRule = {
  id: 'justif_domicile_recent',
  description:
    'Le justificatif de domicile doit être de moins de 6 mois (sauf documents fiscaux : année précédente acceptée)',
  severity: 'error',
  check: (ctx) => {
    const justifDoc = ctx.docs.justif_domicile;
    if (!justifDoc) return null;

    // Garde TYPE : pour les avis d'imposition et taxes foncières, ANTS accepte
    // les documents de l'année précédente toute l'année courante (cas
    // AA-116-WS : bulletin DGFiP des revenus 2025 déposé en juin 2026 →
    // VALIDE, pas trop ancien). On ne déclenche pas l'alerte « > 6 mois »
    // pour ces types ; un avis trop vieux (> 18 mois = > 1 an fiscal complet)
    // reste théoriquement détectable mais c'est rare et préférable de laisser
    // au GOD plutôt que de bloquer en automatique.
    const typeDocument = getFieldValue<string>(justifDoc, 'type_document');
    if (typeDocument && FISCAL_JDC_TYPES.has(typeDocument)) return null;

    const dateRaw = getFieldValue<string>(justifDoc, 'date_document');
    const date = parseDateMaybe(dateRaw);
    if (!date) return null;

    // Garde CONFIANCE : si la date n'a pas été lue directement sur le doc
    // mais inférée à faible confiance (cas typique : bulletin DGFiP où la
    // date d'émission n'est pas écrite en clair, l'OCR devine via indices
    // textuels), on s'abstient — la date est non garantie, le GOD vérifiera.
    const source = getFieldSource(justifDoc, 'date_document');
    const confidence = getFieldConfidence(justifDoc, 'date_document') ?? 0;
    if (source === 'inferred' && confidence < 0.85) return null;

    // Garde §13.12 — émission ≠ souscription. Les justificatifs d'ABONNEMENT
    // (énergie, télécom) portent une date d'ÉMISSION récente ET une date de
    // DÉBUT de contrat ancienne (« titulaire depuis … »). Si l'OCR a confondu
    // les deux (la date retenue coïncide avec la date de début de contrat
    // extraite), elle n'est PAS fiable comme date d'émission → on ne bloque pas
    // sur la fraîcheur (relecture GOD plutôt que faux rejet). Cf. skill §13.12.
    // Cas EL-659-GT (DC-39152) : attestation TotalEnergies émise 06/05/2026,
    // « titulaire depuis 29/09/2023 » — l'OCR avait retenu 2023.
    const dateDebutContrat = parseDateMaybe(getFieldValue<string>(justifDoc, 'date_debut_contrat'));
    if (dateDebutContrat && Math.abs(date.getTime() - dateDebutContrat.getTime()) / (1000 * 60 * 60 * 24) <= 1) {
      return null;
    }

    const months = monthsBetween(date, ctx.now);
    if (months > 6) {
      return makeFinding(
        'justif_domicile_recent',
        'error',
        `Le justificatif de domicile date de ${months.toFixed(1)} mois, au-delà de la limite ANTS de 6 mois. → Fournissez un justificatif de moins de 6 mois (facture d'énergie, d'eau, de téléphone…).`,
        undefined,
        ['justif_domicile'],
      );
    }
    return null;
  },
};

// 12. Signature cession co-titulaire si C.4.1 non vide — POINT D'ATTENTION
//
// L'obligation métier reste vraie (un co-titulaire C.4.1 doit signer la
// cession), MAIS la PRÉSENCE d'une signature est un signal OCR peu fiable
// (case manuscrite, signature qui déborde, scan partiel). Une signature que
// l'OCR ne détecte pas n'est PAS forcément absente — cf. règle maîtresse du
// référentiel CTVO : « une donnée mal lue par l'OCR n'est PAS une donnée
// manquante ». Cas réel EL-659-GT / DC-39152 : les DEUX co-titulaires ont bien
// signé la cession, mais l'OCR lit signature_cotitulaire = false → faux blocage
// MAJEUR sur un dossier valide. On rétrograde donc en point d'attention qui
// invite à une vérification visuelle, au lieu de bloquer la soumission.
const signatureCessionCotitulaire: CrossCheckRule = {
  id: 'signature_cession_cotitulaire',
  description: 'Si un co-titulaire est présent sur la carte grise, sa signature doit figurer sur la cession',
  severity: 'warning',
  check: (ctx) => {
    // Co-titulaire vendeur détecté par l'OCR (C.4.1) OU déclaré manuellement par
    // l'agent via la case dédiée quand l'OCR ne l'a pas lu (case cochée à la main).
    const cotitulaireNom = getFieldValue<string>(ctx.docs.carte_grise, 'cotitulaire_nom');
    if (!cotitulaireNom && !ctx.cotitulaireVendeurManuel) return null;
    const signatureCotit = getFieldValue<boolean>(ctx.docs.cerfa_cession, 'signature_cotitulaire');
    if (signatureCotit !== true) {
      const qui = cotitulaireNom ? `Un co-titulaire (${cotitulaireNom})` : 'Un co-titulaire vendeur';
      return makeFinding(
        'signature_cession_cotitulaire',
        'warning',
        `${qui} figure côté vendeur : vérifiez visuellement que sa signature est bien présente sur le certificat de cession (l'OCR ne la détecte pas automatiquement).`,
        undefined,
        ['cerfa_cession'],
      );
    }
    return null;
  },
};

// 13. Mandat SKIPCAR valide (nom + SIREN)
//
// CDC (Manop image 8) : le mandat DOIT être au nom de SKIPCAR, le mismatch
// est une erreur bloquante.
//
// ⚠ Le champ « N° SIRET » du Cerfa 13757 attend en pratique un SIREN (9
// chiffres). On compare donc sur le SIREN (9 premiers chiffres) — SKIPCAR_SIREN
// (899182182) — et pas sur le SIRET 14 chiffres : le NIC (5 derniers) désigne
// un établissement, n'identifie pas l'entité et peut être du bruit OCR.
//
// Subtilités OCR (anti-faux-positif) :
//  - "extraction incomplète" (nom = SKIPCAR mais SIRET non lu) N'EST PAS un
//    mismatch → INFO god-only (cf. cas 4).
//  - nom = SKIPCAR confirmé + SIREN à UN seul chiffre près → quasi-certainement
//    une erreur de lecture sur le formulaire Skipcar (SIREN constant) : on
//    rétrograde en point d'attention au lieu de bloquer (cas AA-116-WS :
//    889182182 lu pour 899182182). On ne BLOQUE que sur un SIREN réellement
//    divergent (≥ 2 chiffres d'écart, ex. SIRET d'un concurrent recopié).
const countDifferingChars = (a: string, b: string): number =>
  a.length !== b.length ? Math.max(a.length, b.length) : a.split('').reduce((n, ch, i) => (ch === b[i] ? n : n + 1), 0);

const mandatSkipcarValide: CrossCheckRule = {
  id: 'mandat_skipcar_valide',
  description: 'Le mandat doit être au nom de SKIPCAR avec le SIREN 899 182 182',
  severity: 'error',
  check: (ctx) => {
    const mandatDoc = ctx.docs.mandat;
    if (!mandatDoc) return null;
    const mandataireNom = getFieldValue<string>(mandatDoc, 'mandataire_nom');
    const mandataireSiret = getFieldValue<string>(mandatDoc, 'mandataire_siret');

    const nomExtrait = Boolean(mandataireNom);
    const siretExtrait = Boolean(mandataireSiret);
    const nomMatchSkipcar = nomExtrait && normalizeForCompare(mandataireNom!).includes('skipcar');
    const siretNormalized = siretExtrait ? mandataireSiret!.replace(/\D/g, '') : '';
    const sirenExtrait = siretNormalized.slice(0, 9);
    const sirenLisible = sirenExtrait.length === 9;
    // Conforme si le SIRET complet matche OU si le SIREN (9 chiffres) matche.
    const siretMatchSkipcar = siretNormalized === SKIPCAR_SIRET || (sirenLisible && sirenExtrait === SKIPCAR_SIREN);

    // Cas 1 : nom ET SIREN conformes → tout va bien, pas de finding.
    if (nomMatchSkipcar && siretMatchSkipcar) return null;

    // Cas 2 : vrai mismatch — le nom extrait n'est PAS SKIPCAR. Bloquant.
    if (nomExtrait && !nomMatchSkipcar) {
      return makeFinding(
        'mandat_skipcar_valide',
        'error',
        `Le mandat doit désigner SKIPCAR comme mandataire, or on lit « ${mandataireNom} ». → Faites établir le mandat (Cerfa 13757) au nom de SKIPCAR, sinon nous ne sommes pas habilités à traiter le dossier.`,
        undefined,
        ['mandat'],
      );
    }

    // Cas 3 : SIREN lisible mais ≠ SKIPCAR → BLOCAGE. Un SIREN mandataire erroné
    // fait rejeter le dossier à l'ANTS (c'est le SIREN qui identifie le mandataire
    // habilité) : la sévérité reste donc HAUTE même quand c'est probablement une
    // erreur de lecture (asymétrie de risque — cf. retour CheckCTVO AA-116-WS).
    // L'écart de caractères ne module que le MESSAGE, pas la gravité.
    if (siretExtrait && sirenLisible && !siretMatchSkipcar) {
      const sirenDiff = countDifferingChars(sirenExtrait, SKIPCAR_SIREN);
      // 1 seul chiffre d'écart + nom SKIPCAR → très probablement une erreur OCR
      // sur le formulaire Skipcar, mais on impose une confirmation (bloquant).
      if (nomMatchSkipcar && sirenDiff === 1) {
        return makeFinding(
          'mandat_skipcar_valide',
          'error',
          `Le SIREN du mandataire lu (${sirenExtrait}) n'est pas exactement celui de SKIPCAR (${SKIPCAR_SIREN}) — un seul chiffre diffère, probablement une erreur de lecture OCR sur le formulaire Skipcar. MAIS un SIREN mandataire erroné fait rejeter le dossier à l'ANTS : confirmez impérativement le SIREN sur le mandat avant l'envoi (et corrigez-le s'il est réellement faux).`,
          undefined,
          ['mandat'],
        );
      }
      // Vraie divergence (≥ 2 chiffres, ou nom non confirmé) → bloquant net.
      return makeFinding(
        'mandat_skipcar_valide',
        'error',
        `Le SIREN du mandataire (${sirenExtrait}) n'est pas celui de SKIPCAR (${SKIPCAR_SIREN}). → Corrigez le mandat avec les coordonnées SKIPCAR, sinon l'ANTS le refusera.`,
        undefined,
        ['mandat'],
      );
    }

    // Cas 4 : nom = SKIPCAR mais SIRET non lu de façon fiable (non extrait OU
    // moins de 9 chiffres). Le mandat Cerfa 13757 de Skipcar est leur formulaire
    // standard, sur lequel le SIRET est PRÉ-IMPRIMÉ et constant (89918218200027) :
    // un nom « SKIPCAR » confirmé suffit donc à identifier la bonne pièce. Un
    // SIRET simplement non lu par l'OCR (champ souvent minuscule) N'EST PAS une
    // anomalie de dossier — on ne harcèle plus l'agence avec une mineure
    // récurrente (l'outil de référence ne la remonte jamais). On n'alerte QUE
    // sur un SIREN réellement divergent (cas 3). On garde une simple INFO
    // (god-only) comme trace d'audit interne.
    if (nomMatchSkipcar && (!siretExtrait || !sirenLisible)) {
      return makeFinding(
        'mandat_skipcar_valide',
        'info',
        `Mandat au nom de SKIPCAR confirmé ; SIRET non relu par l'OCR (champ pré-imprimé sur le formulaire Skipcar, SIRET constant ${SKIPCAR_SIRET}). Aucune action requise — note interne.`,
        undefined,
        ['mandat'],
      );
    }

    // Cas 5 : ni nom ni SIRET extraits → warning, peut-être un scan dégradé.
    return makeFinding(
      'mandat_skipcar_valide',
      'warning',
      `Le nom et le SIRET du mandataire n'ont pas pu être lus automatiquement. Vérifiez visuellement que le mandat est bien au nom de SKIPCAR (SIRET ${SKIPCAR_SIRET}).`,
      undefined,
      ['mandat'],
    );
  },
};

// 14. Pas de location longue durée / crédit-bail (SKIPCAR non habilité)
const pasLocationNiCreditBail: CrossCheckRule = {
  id: 'pas_location_ni_credit_bail',
  description: "SKIPCAR n'est pas habilité pour les CG entre Loueur et Locataire",
  severity: 'error',
  check: (ctx) => {
    const cerfaImmat = ctx.docs.cerfa_demande_immat;
    if (!cerfaImmat) return null;
    const loueurPresent = getFieldValue<boolean>(cerfaImmat, 'loueur_present');
    const locatairePresent = getFieldValue<boolean>(cerfaImmat, 'locataire_present');
    const creditBail = getFieldValue<boolean>(cerfaImmat, 'credit_bail');
    if (loueurPresent || locatairePresent || creditBail) {
      return makeFinding(
        'pas_location_ni_credit_bail',
        'error',
        "SKIPCAR n'est pas habilité à traiter les cartes grises pour les véhicules en location longue durée ou crédit-bail.",
        undefined,
        ['cerfa_demande_immat'],
      );
    }
    return null;
  },
};

// 15. Ancienne CG papier rose
const ancienneCgPapierRose: CrossCheckRule = {
  id: 'ancienne_cg_papier_rose',
  description: 'Ancienne carte grise au format papier rose — traitement manuel',
  severity: 'warning',
  check: (ctx) => {
    const ancien = getFieldValue<boolean>(ctx.docs.carte_grise, 'ancien_format');
    if (ancien === true) {
      return makeFinding(
        'ancienne_cg_papier_rose',
        'warning',
        'Votre carte grise est au format papier rose. Votre dossier sera traité manuellement par un gestionnaire SKIPCAR — continuez votre saisie.',
        undefined,
        ['carte_grise'],
      );
    }
    return null;
  },
};

// 16. Carte grise étrangère
const cgEtrangere: CrossCheckRule = {
  id: 'cg_etrangere',
  description: 'Carte grise étrangère — traitement manuel',
  severity: 'warning',
  check: (ctx) => {
    const etrangere = getFieldValue<boolean>(ctx.docs.carte_grise, 'etrangere');
    if (etrangere === true) {
      return makeFinding(
        'cg_etrangere',
        'warning',
        "La carte grise détectée semble étrangère. Pas d'inquiétude : ce cas n'est pas géré en automatique, votre dossier sera repris manuellement par un gestionnaire SKIPCAR — vous pouvez continuer votre saisie.",
        undefined,
        ['carte_grise'],
      );
    }
    return null;
  },
};

// 17. SIREN personne morale valide
const personneMoraleSiren: CrossCheckRule = {
  id: 'personne_morale_siren',
  description: "Le SIREN d'une personne morale doit faire exactement 9 chiffres",
  severity: 'error',
  check: (ctx) => {
    const siren = getFieldValue<string>(ctx.docs.carte_grise, 'titulaire_siren');
    if (!siren) return null;
    const cleanSiren = siren.replace(/\s/g, '');
    if (!SIREN_REGEX.test(cleanSiren)) {
      return makeFinding(
        'personne_morale_siren',
        'error',
        `Le SIREN lu « ${siren} » n'est pas valide : un SIREN compte exactement 9 chiffres. → Vérifiez-le sur le Kbis et corrigez-le.`,
        undefined,
        ['carte_grise'],
      );
    }
    return null;
  },
};

// 18. Détection micro-entrepreneur (info non bloquant)
/**
 * Les Kbis du dossier, tous slots confondus.
 *
 * Depuis le dédoublement du slot (vendeur / acquéreur), lire le seul `kbis`
 * laissait échapper les deux nouveaux types : une entreprise RADIÉE côté vendeur
 * passait inaperçue alors qu'elle bloque tout autant la démarche. `kbis` brut
 * reste consulté pour les dossiers antérieurs au routage.
 */
const kbisDocsOf = (ctx: { docs: Partial<Record<string, UploadedDocResult>> }): UploadedDocResult[] =>
  (['kbis_acheteur', 'kbis_vendeur', 'kbis'] as const)
    .map((docType) => ctx.docs[docType])
    .filter((doc): doc is UploadedDocResult => Boolean(doc));

/** Les types Kbis à signaler sur un finding, pour que la tuile concernée le reçoive. */
const KBIS_DOC_TYPES: readonly string[] = ['kbis', 'kbis_vendeur', 'kbis_acheteur'];

const detectionMicroEntrepreneur: CrossCheckRule = {
  id: 'detection_micro_entrepreneur',
  description: 'Micro-entrepreneur détecté : la demande doit être en nom propre',
  severity: 'info',
  check: (ctx) => {
    const kbis = kbisDocsOf(ctx).find((doc) => getFieldValue<boolean>(doc, 'entreprise_individuelle') === true);
    if (kbis) {
      return makeFinding(
        'detection_micro_entrepreneur',
        'info',
        "Micro-entrepreneur / entreprise individuelle détectée. La demande doit être faite en nom propre (l'entreprise individuelle peut être enregistrée en co-titulaire).",
        undefined,
        KBIS_DOC_TYPES,
      );
    }
    return null;
  },
};

// 19. Cohérence plaque saisie en étape 1 vs OCR des pièces (toutes les
// pièces qui portent une immatriculation : CG, cession, demande immat,
// mandat, assurance, CT). Sévérité ERROR car ANTS refuse systématiquement
// si la plaque de la demande ne matche pas celle des pièces — l'agence a
// soit tapé une faute de frappe à l'étape 1, soit déposé les mauvaises
// pièces.
const IMMAT_DOCS: Array<{ docType: CgDocType; label: string }> = [
  { docType: 'carte_grise', label: 'la carte grise' },
  { docType: 'cerfa_cession', label: 'le certificat de cession' },
  { docType: 'cerfa_demande_immat', label: "la demande d'immatriculation" },
  { docType: 'mandat', label: 'le mandat' },
  { docType: 'assurance', label: "l'attestation d'assurance" },
  { docType: 'controle_technique', label: 'le contrôle technique' },
  // Le récépissé de déclaration d'achat porte lui aussi une immatriculation, et
  // rien ne la vérifiait : FL-786-KH (retour du 17/08) avait reçu le récépissé
  // de FQ-723-GM sans que personne ne le voie. Arbitré en alerte seule
  // (`nouveaux_controles_severite`), ce que produit `piece_autre_vehicule` — la
  // règle bloquante `coherence_immat_user_vs_pieces` écarte d'elle-même les
  // pièces d'un autre véhicule, qui relèvent du mauvais fichier, pas de la saisie.
  { docType: 'recepisse_da', label: "le récépissé de déclaration d'achat" },
];

const coherenceImmatUserVsPieces: CrossCheckRule = {
  id: 'coherence_immat_user_vs_pieces',
  description: "L'immatriculation saisie à l'étape 1 doit correspondre à celle des pièces déposées",
  severity: 'error',
  check: (ctx) => {
    const userImmat = ctx.userInput?.regPlate;
    if (!userImmat) return null;
    const userNorm = normalizeForCompare(userImmat);
    if (!userNorm) return null;

    const mismatches: Array<{ label: string; immat: string }> = [];
    for (const { docType, label } of IMMAT_DOCS) {
      const doc = ctx.docs[docType];
      const docImmat = getFieldValue<string>(doc, 'immatriculation');
      if (!docImmat) continue;
      // Pièce d'un AUTRE véhicule glissée par erreur dans le dossier
      // (immat ET vin franchement différents de la CG) : ce n'est pas une
      // incohérence de saisie mais un mauvais fichier. piece_autre_vehicule
      // porte le signalement dédié — on ne pollue pas coherence_immat.
      if (isPieceForAnotherVehicle(ctx, doc)) continue;
      if (normalizeForCompare(docImmat) !== userNorm) {
        mismatches.push({ label, immat: docImmat });
      }
    }
    if (mismatches.length === 0) return null;

    // Garde G.4 (DC-39460) : immatriculation incertaine → suspendu, l'info
    // coherence_immat_skipped_low_confidence prend le relais.
    if (isImmatConfidenceTooLow(ctx)) return null;

    const list = mismatches.map((m) => `${m.label} (${m.immat})`).join(', ');
    // documentTypes = pièces concernées par le mismatch (utilisé pour
    // le code couleur dans AlertsColumn et pour le surlignage côté review).
    const affectedDocTypes: CgDocType[] = IMMAT_DOCS.filter((d) =>
      mismatches.some((m) => m.label.includes(d.label.replace(/^le |^la |^l'/, ''))),
    ).map((d) => d.docType);

    // Toutes les divergences se réduisent-elles à UN caractère OCR-confusable ?
    const allConfusable = mismatches.every((m) => isSingleOcrConfusion(normalizeForCompare(m.immat), userNorm));

    // La carte grise est la pièce de référence pour l'immatriculation. Si
    // elle CONFIRME la saisie de l'agence, une divergence sur une pièce
    // secondaire (cession manuscrite, mandat rempli à la main…) est très
    // probablement une erreur de lecture → mineure, pas majeure.
    const cgImmat = getFieldValue<string>(ctx.docs.carte_grise, 'immatriculation');
    const cgConfirmsUserInput = cgImmat !== null && normalizeForCompare(cgImmat) === userNorm;
    if (cgConfirmsUserInput) {
      return makeFinding(
        'coherence_immat_user_vs_pieces',
        'warning',
        `La carte grise (${cgImmat}) confirme la plaque saisie (${userImmat}), mais ${list} diffère${mismatches.length > 1 ? 'nt' : ''}${allConfusable ? " d'un seul caractère facilement confondu à la lecture" : ''}. Probable erreur OCR — à vérifier visuellement.`,
        ['immatriculation'],
        affectedDocTypes.length > 0 ? affectedDocTypes : undefined,
      );
    }

    // Pas de CG lisible (ou CG divergente) : majeure, SAUF si tout
    // s'explique par une confusion d'un caractère (J/L, O/0…) → mineure.
    return makeFinding(
      'coherence_immat_user_vs_pieces',
      allConfusable ? 'warning' : 'error',
      `La plaque saisie à l'étape 1 (${userImmat}) ne correspond pas à ${list}.${
        allConfusable
          ? " L'écart se limite à un caractère facilement confondu à la lecture — vérifier visuellement la pièce avant de corriger."
          : ' Vérifiez votre saisie OU les pièces déposées — ANTS refusera le dossier sinon.'
      }`,
      ['immatriculation'],
      affectedDocTypes.length > 0 ? affectedDocTypes : undefined,
    );
  },
};

// 20. Check digit du VIN (position 9). Cette vérification n'est OBLIGATOIRE
// qu'en Amérique du Nord (FMVSS 115 — WMI commençant par 1-5). Les
// constructeurs européens (W… Allemagne, V… France, Z… Italie) utilisent
// cette position librement : tester leurs VIN produit des faux positifs
// systématiques (ex: Mercedes WDD1760031V084441, parfaitement valide).
// On ne contrôle donc QUE les VIN nord-américains.
const isNorthAmericanVin = (vin: string): boolean => /^[1-5]/.test(vin);

const vinCheckDigit: CrossCheckRule = {
  id: 'vin_check_digit',
  description: 'Le check digit du VIN doit être valide (VIN nord-américains uniquement)',
  severity: 'warning',
  check: (ctx) => {
    const cgVin = getFieldValue<string>(ctx.docs.carte_grise, 'vin');
    const cessionVin = getFieldValue<string>(ctx.docs.cerfa_cession, 'vin');
    const candidates: Array<{ vin: string; label: string; docType: CgDocType }> = [];
    if (cgVin && isNorthAmericanVin(cgVin)) {
      candidates.push({ vin: cgVin, label: 'la carte grise', docType: 'carte_grise' });
    }
    if (cessionVin && isNorthAmericanVin(cessionVin)) {
      candidates.push({ vin: cessionVin, label: 'le certificat de cession', docType: 'cerfa_cession' });
    }
    if (candidates.length === 0) return null;

    const allInvalid = candidates.every((c) => !validateVinCheckDigit(c.vin));
    if (!allInvalid) return null;

    const list = candidates.map((c) => `${c.label} (${c.vin})`).join(' et ');
    return makeFinding(
      'vin_check_digit',
      'warning',
      `Le VIN nord-américain ne passe pas la vérification du check digit sur ${list}. À relire — possible erreur OCR.`,
      ['vin'],
      candidates.map((c) => c.docType),
    );
  },
};

// 21. (RETIRÉE — patch DC-40245) La règle "cg_recente_15j" est supprimée : la
// seule date d'émission de la carte grise ne permet PAS de distinguer une CG
// fraîchement rééditée (changement d'adresse / duplicata juste avant la vente,
// seul cas visé par le délai ANTS des 15 jours) d'un simple achat récent du
// véhicule par le vendeur. Pour TOUTE revente rapide (véhicule détenu quelques
// semaines/mois), l'émission tombe naturellement dans les jours/semaines avant
// la cession → la fenêtre 15-90 j flaggait chaque revente rapide à tort
// (ex DC-40245 : CG émise 88 j avant la cession, revente parfaitement normale).
// Une réédition tardive éventuelle reste repérable à l'œil par le relecteur GOD.

// 22. (RETIRÉE — arbitrage François 2026-09-24) La règle "ct_valide_via_cg_x1"
// affichait « CT valide via la carte grise (X.1) — pas de PV requis ». X.1 ne
// prouve pas un CT de moins de 6 mois : au-delà de 4 ans le PV est toujours
// exigé (règle 7). Cas FT-445-ZH passé sans CT.

// 23. Champ "opération d'immatriculation" du mandat non rempli → l'ANTS
// peut rejeter le mandat. Warning car certains scans dégradés masquent ce
// champ alors qu'il est bien rempli sur le papier.
const mandatOperationTypeVide: CrossCheckRule = {
  id: 'mandat_operation_type_vide',
  description: 'Le champ "opération d\'immatriculation" du mandat ne doit pas être vide',
  severity: 'warning',
  check: (ctx) => {
    const mandat = ctx.docs.mandat;
    if (!mandat) return null;
    // Mandat d'un AUTRE véhicule glissé dans le dossier (immat + vin
    // franchement différents de la CG) : ne pas analyser ses champs ici,
    // piece_autre_vehicule porte le signalement. Cas DC-40000 (mandat BMW
    // EH-668-NM dans un dossier Opel GN-079-RQ).
    if (isPieceForAnotherVehicle(ctx, mandat)) return null;
    const operationType = getFieldValue<string>(mandat, 'operation_type');
    if (operationType && operationType.trim().length > 0) return null;
    // Si le champ existe explicitement comme "not_found", on remonte le warning.
    const field = mandat.ocrResponse.fields['operation_type'];
    if (!field) return null;
    return makeFinding(
      'mandat_operation_type_vide',
      'warning',
      "Le champ « opération d'immatriculation » du mandat ressort vide ou illisible. → Vérifiez visuellement qu'il est bien rempli (ex. « changement de titulaire ») avant l'envoi : l'ANTS peut refuser un mandat sans cette mention.",
      undefined,
      ['mandat'],
    );
  },
};

// 24. Case "Je suis informé(e) que… je suis dans l'obligation d'assurer le
// véhicule" du mandat. L'ANTS exige cet engagement, mais — décision François
// 2026-07 — son absence est classée MINEURE (point d'attention), pas bloquante :
// c'est une case à faire cocher, corrigeable, et la détection d'une croix pâle
// reste un signal OCR faillible. On émet donc toujours un `warning` (message plus
// ferme si l'OCR est CONFIANT que la case est vide, plus prudent si la lecture
// est incertaine).
const CASE_ASSURANCE_CONFIDENCE_THRESHOLD = 0.85;
const mandatCaseAssuranceNonCochee: CrossCheckRule = {
  id: 'mandat_case_assurance',
  description: "La case d'engagement d'assurance du mandat doit être cochée",
  severity: 'warning',
  check: (ctx) => {
    const mandat = ctx.docs.mandat;
    if (!mandat) return null;
    const cochee = getFieldValue<boolean>(mandat, 'case_assurance_cochee');
    if (cochee !== false) return null;
    const confidence = getFieldConfidence(mandat, 'case_assurance_cochee') ?? 0;
    if (confidence >= CASE_ASSURANCE_CONFIDENCE_THRESHOLD) {
      return makeFinding(
        'mandat_case_assurance',
        'warning',
        "La case d'engagement d'assurance du mandat (Cerfa 13757) n'est pas cochée. → Faites cocher cette case avant l'envoi : l'ANTS rejette un mandat sans cet engagement d'assurance.",
        undefined,
        ['mandat'],
      );
    }
    return makeFinding(
      'mandat_case_assurance',
      'warning',
      "La case d'engagement d'assurance du mandat ne ressort pas clairement cochée (lecture incertaine). → Vérifiez visuellement qu'elle est bien cochée sur le papier : l'ANTS peut refuser un mandat sans cet engagement.",
      undefined,
      ['mandat'],
    );
  },
};

// 25. Kbis : entreprise radiée → blocage (l'entreprise n'a plus
// d'existence légale, elle ne peut pas être titulaire de la carte grise).
const kbisEntrepriseRadiee: CrossCheckRule = {
  id: 'kbis_entreprise_radiee',
  description: "L'entreprise du Kbis ne doit pas être radiée",
  severity: 'error',
  check: (ctx) => {
    // Toutes parties confondues : une société radiée côté VENDEUR ne peut pas
    // davantage céder le véhicule qu'une société radiée ne peut l'acquérir.
    const kbis = kbisDocsOf(ctx).find((doc) => getFieldValue<string>(doc, 'date_radiation'));
    if (!kbis) return null;
    const dateRadiationRaw = getFieldValue<string>(kbis, 'date_radiation');
    return makeFinding(
      'kbis_entreprise_radiee',
      'error',
      `L'entreprise du Kbis est radiée depuis le ${dateRadiationRaw} : elle n'a plus d'existence légale et ne peut pas devenir titulaire de la carte grise. → Une société active (Kbis à jour) doit porter la demande, sinon l'ANTS rejettera le dossier.`,
      undefined,
      KBIS_DOC_TYPES,
    );
  },
};

// ── Garde de cascade sur l'immatriculation (patchs DC-39462 G.1/H.1 +
// DC-39460 G.4) ─────────────────────────────────────────────────────────
// Si l'immatriculation extraite des pièces maîtresses (CG, cession) est
// incertaine, toutes les règles qui en DÉPENDENT produisent des faux
// positifs en cascade : les comparaisons MiSIV (mauvais véhicule interrogé)
// comme les cohérences inter-pièces (mauvaise lecture comparée). En dessous
// du seuil, ces règles sont suspendues et des infos dédiées l'expliquent.
const IMMAT_CONFIDENCE_THRESHOLD = 0.92;

const lowestImmatConfidence = (ctx: CrossCheckContext): number | null => {
  const confidences = [
    getFieldConfidence(ctx.docs.carte_grise, 'immatriculation'),
    getFieldConfidence(ctx.docs.cerfa_cession, 'immatriculation'),
  ].filter((c): c is number => c !== null);
  if (confidences.length === 0) return null;
  return Math.min(...confidences);
};

const isImmatConfidenceTooLow = (ctx: CrossCheckContext): boolean => {
  const min = lowestImmatConfidence(ctx);
  return min !== null && min < IMMAT_CONFIDENCE_THRESHOLD;
};

// Même doctrine que l'immat (§13.5) appliquée au VIN : un VIN lu avec une
// confiance basse (scan pivoté, papier thermique, manuscrit) ne doit pas
// déclencher une fausse divergence inter-pièces. « donnée mal lue ≠ donnée
// divergente ». Cas réel DC-40245 : cession pivotée 90° mal redressée → VIN lu
// "VF13PNB8HTC8727B4" à 55 % alors que carte grise (97 %), mandat, 13750 et
// contrôle technique confirment tous "VF3PN8HTC88722824". Seuil aligné 0.92.
const VIN_CONFIDENCE_THRESHOLD = IMMAT_CONFIDENCE_THRESHOLD;

const lowestVinConfidence = (ctx: CrossCheckContext): number | null => {
  const confidences = [
    getFieldConfidence(ctx.docs.carte_grise, 'vin'),
    getFieldConfidence(ctx.docs.cerfa_cession, 'vin'),
  ].filter((c): c is number => c !== null);
  if (confidences.length === 0) return null;
  return Math.min(...confidences);
};

const isVinCgCessionConfidenceTooLow = (ctx: CrossCheckContext): boolean => {
  const min = lowestVinConfidence(ctx);
  return min !== null && min < VIN_CONFIDENCE_THRESHOLD;
};

// Lot 3 #2 — Réponse MiSIV stubée en environnement non-prod (dev / staging).
//
// CONTEXTE BACKEND (cf. misivController.getECG + resolveMisivRegPlate) :
// Hors prod, le backend SUBSTITUE la plaque demandée par un stub fixe
// (BL-726-DJ par défaut) avant d'interroger le TMS — pour ne pas spammer
// l'env dev avec des plaques de test réelles. Conséquence : `vehicleDetails`
// renvoie TOUJOURS la même réponse (VIN VF3WC8HR0BW019017 Peugeot, MEC
// associée), quel que soit le véhicule réellement uploadé par l'agence.
//
// Sans cette garde, TOUS les dossiers de recette en dev sortiraient avec
// une fausse majeure vin_misiv_mismatch + une fausse mineure
// mec_misiv_mismatch.
//
// ⚠ APPROCHE : on lit le flag `is_stub` que le BACKEND positionne lui-même
// dans `vehicleDetails.is_stub` selon process.env.NODE_ENV. Plus de liste
// de VINs en dur côté front, plus de dépendance à `import.meta.env.DEV`
// (qui ne reflète pas toujours l'env du backend, ex. front dev + back prod).
// Si le backend dit "j'ai stubé" → on rétrograde l'alerte. Sinon comportement
// standard, y compris si on tombe par hasard sur un véhicule Peugeot dont le
// VIN matche par coïncidence — le backend prod ne mettra jamais is_stub.
const isMisivStubResponse = (ctx: CrossCheckContext): boolean => {
  const flag = ctx.taxData?.vehicleDetails?.is_stub;
  // DEBUG temporaire — pour diagnostiquer pourquoi la rétrogradation
  // vin_misiv_mismatch en INFO ne déclenche pas chez Luc en recette
  // DC-39628. À retirer une fois le bug isolé.
  // eslint-disable-next-line no-console
  console.log('[MISIV-FRONT-DEBUG] isMisivStubResponse', {
    is_stub_flag: flag,
    flag_type: typeof flag,
    vehicleDetails_keys: ctx.taxData?.vehicleDetails ? Object.keys(ctx.taxData.vehicleDetails) : null,
    taxData_present: Boolean(ctx.taxData),
    misiv_vin: ctx.taxData?.vehicleDetails?.VIN ?? null,
  });
  return flag === true;
};

// Info G.4 (DC-39460) : quand les règles de cohérence immat sont suspendues
// pour confiance insuffisante ET qu'une divergence existait, on l'explique
// au relecteur avec les valeurs lues — au lieu d'une majeure catégorique sur
// une lecture douteuse, ou d'un silence inexpliqué.
const coherenceImmatSkippedLowConfidence: CrossCheckRule = {
  id: 'coherence_immat_skipped_low_confidence',
  description: "Les cohérences immatriculation sont suspendues quand l'OCR est incertain",
  severity: 'info',
  check: (ctx) => {
    if (!isImmatConfidenceTooLow(ctx)) return null;

    // N'informe que s'il y avait matière à alerte : au moins deux valeurs
    // distinctes parmi la saisie agence et les pièces porteuses d'immat.
    const candidates: Array<{ label: string; value: string }> = [];
    const userImmat = ctx.userInput?.regPlate;
    if (userImmat) candidates.push({ label: 'saisie', value: userImmat });
    for (const { docType, label } of IMMAT_DOCS) {
      const docImmat = getFieldValue<string>(ctx.docs[docType], 'immatriculation');
      if (docImmat) candidates.push({ label: label.replace(/^le |^la |^l'/, ''), value: docImmat });
    }
    const distinct = new Set(candidates.map((c) => normalizeForCompare(c.value)));
    if (distinct.size <= 1) return null;

    const list = candidates.map((c) => `${c.label} : ${c.value}`).join(' · ');
    const min = lowestImmatConfidence(ctx) ?? 0;
    return makeFinding(
      'coherence_immat_skipped_low_confidence',
      'info',
      `Cohérence immatriculation suspendue — confiance OCR : ${min.toFixed(2)}. Valeurs lues (${list}) — confirmer la plaque visuellement, la carte grise fait foi.`,
      ['immatriculation'],
      ['carte_grise', 'cerfa_cession'],
    );
  },
};

// G.5 (DC-39460) : Parcours A — signal informatif. La règle métier
// (cédant cession = pro, C.1 CG = particulier → récépissé DA requis) est
// dédoublée avec recepisseDaManquantParcoursA qui porte le blocage
// actionnable. Ici on s'efface tant que la pièce manque (laisse la règle
// blocante parler) et on confirme le parcours en INFO une fois la pièce
// déposée — l'agence sait alors que le dossier est correctement classé
// en Parcours A pour la soumission ANTS.
const parcoursARequis: CrossCheckRule = {
  id: 'parcours_a_requis',
  description: "Une cession par un professionnel intermédiaire exige un récépissé de déclaration d'achat",
  severity: 'info',
  check: (ctx) => {
    const cession = ctx.docs.cerfa_cession;
    const cg = ctx.docs.carte_grise;
    if (!cession || !cg) return null;

    const vendeurEstPersonneMorale = getFieldValue<boolean>(cession, 'vendeur_est_personne_morale') === true;
    const vendeurSiret = getFieldValue<string>(cession, 'vendeur_siret');
    const cessionVendeurPro = vendeurEstPersonneMorale || Boolean(vendeurSiret && vendeurSiret.trim());

    const cgTitulaireSiren = getFieldValue<string>(cg, 'titulaire_siren');
    const cgTitulaireParticulier = !cgTitulaireSiren || !cgTitulaireSiren.trim();

    if (!cessionVendeurPro || !cgTitulaireParticulier) return null;

    // Garde VENTE DIRECTE (cf. recepisseDaManquantParcoursA) : un pro qui
    // revend son PROPRE véhicule n'est pas un intermédiaire → pas de
    // Parcours A. Évite d'étiqueter à tort le dossier en Parcours A quand le
    // SIREN titulaire CG n'a pas été extrait. Cas DC-39988.
    if (isVendeurSameAsCgTitulaire(ctx)) return null;

    // Le récépissé n'est pas encore déposé → on laisse
    // recepisseDaManquantParcoursA porter le blocage (mêmes conditions,
    // message orienté action). Pas de double alerte.
    if (!ctx.docs.recepisse_da) return null;

    const vendeurNom =
      getFieldValue<string>(cession, 'vendeur_raison_sociale') ?? getFieldValue<string>(cession, 'vendeur_nom') ?? '?';
    const cgTitulaireNom = getFieldValue<string>(cg, 'titulaire_nom') ?? '?';
    return makeFinding(
      'parcours_a_requis',
      'info',
      `Parcours A confirmé : cédant professionnel (${vendeurNom}${
        vendeurSiret ? `, SIRET ${vendeurSiret}` : ''
      }), C.1 carte grise particulier (${cgTitulaireNom}). Récépissé de déclaration d'achat présent — pièce ANTS requise OK.`,
      undefined,
      ['cerfa_cession', 'carte_grise', 'recepisse_da'],
    );
  },
};

// G.6 (DC-39460) : le numéro de formule manquant sur la cession.
//
// Le n° de formule relie le dossier à la carte grise côté ANTS et peut bloquer
// la soumission s'il manque sur la cession. MAIS la carte grise (qui fait foi)
// en porte la valeur : c'est donc un point d'attention RECOPIABLE, jamais un
// blocage. On reste volontairement en MINEURE car le n° de la cession, s'il est
// absent à l'OCR, peut tout aussi bien être une simple erreur de lecture d'un
// n° réellement présent (cf. §13 « donnée mal lue ≠ donnée manquante » et piège
// pré-imprimé AA-116-WS où le "20" amorce d'année est pris pour un remplissage).
//
// Garde anti-bruit : on ne signale QUE si la carte grise fournit une formule
// valide à recopier — sinon il n'y a aucune valeur de référence exploitable et
// on s'abstient.
//
// ⚠ DÉCISION CONFIRMÉE (AA-116-WS, juin 2026) — NE PAS rebasculer en MAJEURE.
// CheckCTVO proposait de classer ce cas en MAJEURE (rejet ANTS possible).
// Tranché avec l'agence sur la base du vécu terrain ANTS : le champ vide sur la
// cession est TOLÉRÉ tant que la carte grise (qui fait foi) porte le n° — c'est
// un recoupement recopiable, pas une pièce/donnée requise absente. On reste donc
// volontairement en MINEURE (≠ cases acquéreur / SIREN mandat, eux en MAJEURE).
//
// Numéro de formule SIV — format partagé avec le mapping de création (cf.
// ./numeroFormule) : année "20xx" + 2 lettres + 4-8 chiffres. Le préfixe "20"
// rejette les valeurs aberrantes lues sur une autre zone (ex. "F2AX28QT2HZAA0518").
const FORMULE_FORMAT = NUMERO_FORMULE_SIV;
const looksLikePreprintedFormule = (raw: string | null): boolean => {
  if (!raw) return true; // rien d'extrait → on ne peut rien affirmer
  const trimmed = raw.replace(/\s+/g, '').toUpperCase();
  if (trimmed.length >= 10 && FORMULE_FORMAT.test(trimmed)) return false;
  // "20", "2026", "20__" ou tout résidu trop court : du pré-imprimé.
  return trimmed.length < 10;
};

const CESSION_READ_CONFIDENCE_MIN = 0.2;

/**
 * La cession est-elle GLOBALEMENT illisible ? Vrai uniquement si AUCUN champ
 * texte/numérique n'a été lu avec un minimum de confiance (photo floue OU pivotée
 * non redressée : l'OCR renvoie tout en null, ou des valeurs à confiance 0 /
 * source not_found). On émet alors UN message clair « fournir un meilleur scan »
 * (skill §3-bis) plutôt qu'une cascade de fausses alertes champ-par-champ.
 *
 * Les booléens inférés (cotitulaire_present=false @ conf 0, etc.) ne comptent PAS
 * comme « lu » : un doc vierge ne doit pas paraître lisible parce que le modèle a
 * deviné un false. `cession` doit être défini.
 */
const isCessionGloballyUnreadable = (cession: UploadedDocResult): boolean => {
  const keys = Object.keys(cession.ocrResponse?.fields ?? {});
  if (keys.length === 0) return true;
  return keys.every((key) => {
    const value = getFieldValue<string | number | boolean>(cession, key);
    const conf = getFieldConfidence(cession, key) ?? 0;
    const hasReadableValue =
      typeof value !== 'boolean' &&
      value !== null &&
      value !== undefined &&
      !(typeof value === 'string' && value.trim() === '');
    return !(hasReadableValue && conf >= CESSION_READ_CONFIDENCE_MIN);
  });
};

// La cession est présente mais l'OCR n'a rien pu en lire (scan flou, ou photo
// pivotée que le redressement automatique n'a pas corrigée). Plutôt que de
// laisser les règles champ-par-champ crier des faux positifs, on émet un seul
// point d'attention de relecture (skill §3-bis : « cession illisible → MINEURE »).
const cessionIllisible: CrossCheckRule = {
  id: 'cession_illisible',
  description: 'Le certificat de cession doit être lisible',
  severity: 'warning',
  check: (ctx) => {
    const cession = ctx.docs.cerfa_cession;
    if (!cession) return null;
    if (!isCessionGloballyUnreadable(cession)) return null;
    return makeFinding(
      'cession_illisible',
      'warning',
      "Le certificat de cession n'a pas pu être lu (aucune information exploitable : immatriculation, vendeur, acquéreur, date…). La photo est probablement floue, mal cadrée ou prise de travers. → Fournissez un nouveau scan net, bien à plat et droit, avant l'envoi.",
      [],
      ['cerfa_cession'],
    );
  },
};

const numeroFormuleManquantCession: CrossCheckRule = {
  id: 'numero_formule_manquant_cession',
  description: 'Le numéro de formule doit être renseigné sur la cession (cohérence carte grise)',
  severity: 'warning',
  check: (ctx) => {
    const cession = ctx.docs.cerfa_cession;
    if (!cession) return null;
    // Cession globalement illisible → géré par `cession_illisible` (un seul
    // message de relecture) ; on n'ajoute pas un faux « n° de formule manquant ».
    if (isCessionGloballyUnreadable(cession)) return null;

    // 1) La cession porte déjà un n° de formule au format SIV valide → rien.
    const cessionFormule = (getFieldValue<string>(cession, 'numero_formule') ?? '').replace(/\s+/g, '').toUpperCase();
    if (FORMULE_FORMAT.test(cessionFormule)) return null;

    // 2) On ne signale QUE s'il existe une formule valide à recopier depuis la
    //    carte grise (qui fait foi). Sans valeur de référence exploitable, on
    //    s'abstient pour ne pas générer de bruit.
    const cgFormule = (getFieldValue<string>(ctx.docs.carte_grise, 'numero_formule') ?? '')
      .replace(/\s+/g, '')
      .toUpperCase();
    if (!FORMULE_FORMAT.test(cgFormule)) return null;

    // 3) DISTINCTION « absent » vs « présent mais mal lu » (skill §13 — donnée
    //    mal lue par l'OCR ≠ donnée manquante). Un n° de formule réel a la
    //    signature `YYYY[A-Z]{2}…` : s'il manque au format strict mais qu'il
    //    porte au moins une LETTRE et reste substantiel (≥ 8 car.), l'OCR a bien
    //    lu un n° RÉEL, juste de travers (cas HH-975-PP : lu brut "2012026AB256",
    //    garbled, conf 65%, le n° est très lisible sur la cession). On NE dit PAS
    //    « n'apparaît pas » — simple INFO de relecture, jamais un point
    //    d'attention. Un blob purement numérique ("1234567890") ou un résidu
    //    pré-imprimé ("20") n'a PAS cette signature → traité comme illisible (4).
    const cessionFormuleRaw = getFieldValue<string>(cession, 'numero_formule');
    const cessionRawNorm = (cessionFormuleRaw ?? '').replace(/\s+/g, '').toUpperCase();
    const looksLikeMisreadFormule =
      cessionRawNorm.length >= 8 && /[A-Z]/.test(cessionRawNorm) && /\d/.test(cessionRawNorm);
    if (looksLikeMisreadFormule) {
      return makeFinding(
        'numero_formule_manquant_cession',
        'info',
        `Le numéro de formule est présent sur le certificat de cession mais la lecture OCR est incertaine (lu « ${cessionFormuleRaw} », la carte grise fait foi : ${cgFormule}). → Relecture visuelle facultative ; aucune action si le n° de la cession correspond bien à la carte grise.`,
        ['numero_formule'],
        ['cerfa_cession', 'carte_grise'],
      );
    }

    // 4) Champ réellement vide ou résidu pré-imprimé → point d'attention,
    //    JAMAIS bloquant : couvre le cas DC-39460 (case non cochée, n° absent)
    //    comme AA-116-WS (case OUI cochée mais n° pré-imprimé "20"). On reste en
    //    mineure : la CG fait foi et le n° est recopiable.
    return makeFinding(
      'numero_formule_manquant_cession',
      'warning',
      `Le numéro de formule n'apparaît pas (ou est illisible) sur le certificat de cession alors qu'il figure sur la carte grise (${cgFormule}). Ce numéro est requis sur la cession pour la soumission ANTS. → Vérifiez la cession : s'il est bien vide, recopiez-le depuis le coupon détachable de la carte grise.`,
      ['numero_formule'],
      ['cerfa_cession', 'carte_grise'],
    );
  },
};

const numeroFormuleManquant13750: CrossCheckRule = {
  id: 'numero_formule_manquant_13750',
  description: "Le numéro de formule doit être renseigné sur la demande d'immat quand la case OUI est cochée",
  severity: 'error',
  check: (ctx) => {
    const demande = ctx.docs.cerfa_demande_immat;
    if (!demande) return null;
    if (getFieldValue<boolean>(demande, 'case_presence_formule') !== true) return null;
    const numeroFormule = getFieldValue<string>(demande, 'numero_formule');
    if (numeroFormule && numeroFormule.trim() && !looksLikePreprintedFormule(numeroFormule)) return null;
    if (looksLikePreprintedFormule(numeroFormule)) return null;
    return makeFinding(
      'numero_formule_manquant_13750',
      'error',
      "La case « OUI — numéro de formule » est cochée sur la demande d'immatriculation (13750*07) mais le numéro de formule n'est pas renseigné. → Complétez-le (il figure sur le coupon détachable / volet de la carte grise), sinon l'ANTS rejettera le dossier.",
      ['numero_formule'],
      ['cerfa_demande_immat'],
    );
  },
};

// 24-bis (AA-116-WS) — Cerfa 15776, cases de certification du NOUVEAU
// PROPRIÉTAIRE. Côté acquéreur, la cession comporte DEUX cases à cocher
// obligatoires :
//   ☐ « certifie acquérir le véhicule désigné ci-dessus »
//   ☐ « certifie avoir été informé de la situation administrative du véhicule »
//      (gage, opposition(s)…).
// Si l'une des deux est vide, l'ANTS peut rejeter la cession (engagement de
// l'acquéreur incomplet). Cas réel AA-116-WS : les deux cases acquéreur vides.
//
// SÉVÉRITÉ HAUTE par principe (retour CheckCTVO) : une case de certification non
// cochée est un BLOCAGE ANTS, donc MAJEURE dès que le modèle lit la case comme
// VIDE (=== false), quelle que soit la confiance. La confiance ne module que le
// WORDING (on ajoute « lecture à confirmer » quand elle est basse), pas la
// gravité — asymétrie de risque : un faux bloquant coûte une vérif visuelle, un
// vrai bloquant manqué coûte un rejet ANTS. Un champ NON extrait (null/undefined)
// ne déclenche RIEN — absence de preuve, pas preuve d'absence (le modèle met
// source:"not_found" quand la zone est illisible/absente, pas false).
const CASE_CERTIF_ACQUEREUR_CONF_THRESHOLD = 0.85;
const casesCertificationAcquereurManquantes: CrossCheckRule = {
  id: 'cases_certification_acquereur_manquantes',
  description: 'Les cases de certification du nouveau propriétaire sur la cession doivent être cochées',
  severity: 'error',
  check: (ctx) => {
    const cession = ctx.docs.cerfa_cession;
    if (!cession) return null;

    const certifAcquerir = getFieldValue<boolean>(cession, 'case_acquereur_certifie_acquerir');
    const certifInforme = getFieldValue<boolean>(cession, 'case_acquereur_certifie_informe');

    // Seules les cases LUES explicitement comme vides (=== false) comptent.
    const manquantes = [
      certifAcquerir === false ? '« certifie acquérir le véhicule »' : null,
      certifInforme === false ? '« certifie avoir été informé de la situation administrative »' : null,
    ].filter((m): m is string => m !== null);
    if (manquantes.length === 0) return null;

    const confidences = [
      certifAcquerir === false ? (getFieldConfidence(cession, 'case_acquereur_certifie_acquerir') ?? 0) : null,
      certifInforme === false ? (getFieldConfidence(cession, 'case_acquereur_certifie_informe') ?? 0) : null,
    ].filter((c): c is number => c !== null);
    const minConfidence = confidences.length ? Math.min(...confidences) : 0;

    const libelle = manquantes.join(' et ');
    const plusieurs = manquantes.length === 2;
    const incertain = minConfidence < CASE_CERTIF_ACQUEREUR_CONF_THRESHOLD;
    // La confiance basse n'ajoute qu'une réserve de lecture — la sévérité reste error.
    const reserveLecture = incertain ? " (lecture automatique à confirmer visuellement sur l'original)" : '';
    const constat = plusieurs
      ? 'les deux cases de certification du nouveau propriétaire ne ressortent pas cochées'
      : `la case ${libelle} (nouveau propriétaire) ne ressort pas cochée`;
    return makeFinding(
      'cases_certification_acquereur_manquantes',
      'error',
      `Sur le certificat de cession, ${constat}${reserveLecture}. C'est un engagement obligatoire de l'acquéreur : sans lui, l'ANTS peut rejeter la cession. → Faites cocher ${
        plusieurs ? `ces cases (${libelle})` : 'cette case'
      } avant l'envoi.`,
      ['case_acquereur_certifie_acquerir', 'case_acquereur_certifie_informe'],
      ['cerfa_cession'],
    );
  },
};

const misivSkippedLowConfidence: CrossCheckRule = {
  id: 'misiv_skipped_low_confidence',
  description: "Les vérifications MiSIV sont suspendues quand l'immatriculation OCR est incertaine",
  severity: 'info',
  check: (ctx) => {
    if (!ctx.taxData?.vehicleDetails) return null;
    if (!isImmatConfidenceTooLow(ctx)) return null;
    const min = lowestImmatConfidence(ctx) ?? 0;
    return makeFinding(
      'misiv_skipped_low_confidence',
      'info',
      `Vérifications MiSIV suspendues — confiance immatriculation : ${min.toFixed(2)}. Confirmer la plaque visuellement avant de comparer au SIV.`,
      ['immatriculation'],
      ['carte_grise', 'cerfa_cession'],
    );
  },
};

// 27. Parcours A — chronologie du récépissé de déclaration d'achat.
//
// CAS RÉEL DC-39686 (FC-331-CR) que l'app ne détectait pas : LSF CAR (pro)
// revend à un particulier le 06/06/2026 à 09h30, mais son récépissé DA
// déclare l'achat le 06/06/2026 à 10h45 — soit APRÈS la revente. ANTS
// rejette : techniquement le pro a vendu avant d'avoir déclaré l'achat.
//
// Règle : en Parcours A, la date+heure d'achat du récépissé DA doit être
// ANTÉRIEURE (ou égale) à la date+heure de cession au client final.
// Si l'heure manque d'un côté, on compare au jour près et on signale en
// point d'attention l'absence d'heure (la chronologie fine est non
// vérifiable). Si l'achat est strictement postérieur → BLOCAGE.
const chronologieRecepisseDa: CrossCheckRule = {
  id: 'chronologie_recepisse_da',
  description: "En Parcours A, la date/heure d'achat du récépissé DA doit être antérieure à la cession au client final",
  severity: 'error',
  check: (ctx) => {
    const da = ctx.docs.recepisse_da;
    const cession = ctx.docs.cerfa_cession;
    if (!da || !cession) return null;

    const dateAchat = parseDateMaybe(getFieldValue<string>(da, 'date_achat'));
    const dateVente = parseDateMaybe(getFieldValue<string>(cession, 'date_vente'));
    if (!dateAchat || !dateVente) return null;

    const heureAchat = getFieldValue<string>(da, 'heure_achat');
    const heureVente = getFieldValue<string>(cession, 'heure_vente');

    // Combine date + heure quand l'heure est disponible des deux côtés.
    const toTimestamp = (d: Date, heure: string | null): { ts: number; hasTime: boolean } => {
      if (!heure || !/^\d{1,2}[:hH]\d{2}$/.test(heure)) return { ts: d.getTime(), hasTime: false };
      const [h, m] = heure.split(/[:hH]/).map((n) => parseInt(n, 10));
      const withTime = new Date(d);
      withTime.setHours(h, m, 0, 0);
      return { ts: withTime.getTime(), hasTime: true };
    };

    const achat = toTimestamp(dateAchat, heureAchat);
    const vente = toTimestamp(dateVente, heureVente);
    const bothHaveTime = achat.hasTime && vente.hasTime;

    const iso = (d: Date): string => d.toISOString().slice(0, 10);

    // Achat strictement postérieur à la vente → blocage chronologique.
    if (achat.ts > vente.ts) {
      const achatLabel = `${iso(dateAchat)}${achat.hasTime ? ` à ${heureAchat}` : ''}`;
      const venteLabel = `${iso(dateVente)}${vente.hasTime ? ` à ${heureVente}` : ''}`;
      return makeFinding(
        'chronologie_recepisse_da',
        'error',
        `Chronologie Parcours A incohérente : l'achat déclaré sur le récépissé DA (${achatLabel}) est postérieur à la cession au client final (${venteLabel}). Le professionnel aurait revendu avant d'avoir déclaré l'achat. Obtenir un récépissé DA corrigé avec une date/heure d'achat antérieure à la cession avant soumission ANTS.`,
        ['date_achat', 'heure_achat'],
        ['recepisse_da', 'cerfa_cession'],
      );
    }

    // Même jour mais une heure manque → impossible de trancher finement.
    if (iso(dateAchat) === iso(dateVente) && !bothHaveTime) {
      return makeFinding(
        'chronologie_recepisse_da',
        'warning',
        `Récépissé DA et cession sont datés du même jour (${iso(dateVente)}) mais l'heure manque ${
          achat.hasTime ? 'sur la cession' : vente.hasTime ? 'sur le récépissé DA' : 'sur les deux pièces'
        } — la chronologie d'achat avant revente ne peut pas être vérifiée. Confirmer visuellement que l'achat précède la cession.`,
        ['date_achat', 'heure_achat'],
        ['recepisse_da', 'cerfa_cession'],
      );
    }

    return null;
  },
};

// 28. Parcours A — récépissé DA manquant quand le parcours est déclenché.
// Complète parcoursARequis : ce dernier signale le besoin de Parcours A,
// celle-ci vérifie spécifiquement que le récépissé DA a bien été déposé.
const recepisseDaManquantParcoursA: CrossCheckRule = {
  id: 'recepisse_da_manquant_parcours_a',
  description: "En Parcours A, le récépissé de déclaration d'achat est obligatoire",
  severity: 'error',
  check: (ctx) => {
    const cession = ctx.docs.cerfa_cession;
    const cg = ctx.docs.carte_grise;
    if (!cession || !cg) return null;

    const vendeurEstPersonneMorale = getFieldValue<boolean>(cession, 'vendeur_est_personne_morale') === true;
    const vendeurSiret = getFieldValue<string>(cession, 'vendeur_siret');
    const cessionVendeurPro = vendeurEstPersonneMorale || Boolean(vendeurSiret && vendeurSiret.trim());
    const cgTitulaireSiren = getFieldValue<string>(cg, 'titulaire_siren');
    const cgTitulaireParticulier = !cgTitulaireSiren || !cgTitulaireSiren.trim();

    // Garde VENTE DIRECTE : si le cédant de la cession EST le titulaire de la
    // carte grise, il n'y a aucun intermédiaire professionnel → pas de
    // Parcours A, donc pas de récépissé DA exigible. Indispensable quand le
    // SIREN titulaire de la CG n'a pas pu être extrait (photo) et que
    // cgTitulaireParticulier vaut true à tort. Cas DC-39988 : AGVV (pro)
    // titulaire C.1 revend à SCEA LA FERME DE PLACHAMP → vente directe.
    if (isVendeurSameAsCgTitulaire(ctx)) return null;

    // Parcours A déclenché mais aucun récépissé DA dans le dossier.
    if (cessionVendeurPro && cgTitulaireParticulier && !ctx.docs.recepisse_da) {
      // ⚠ `documentTypes` ne liste QUE le récépissé. Les pièces citées ici sont
      // marquées « erronées » sur la grille de l'agence : y laisser la cession
      // et la carte grise faisait corriger deux pièces qui n'avaient rien —
      // EQ-060-GD (retour du 18/08), « tu mets carte grise, certificat de
      // cession erronés alors qu'il manque juste le récépissé ». Arbitrage
      // `parcours_alternatif_pieces` du 09/09 : aucune autre pièce n'est
      // marquée. Les deux Cerfas restent nommés dans le MESSAGE, qui explique
      // d'où vient le Parcours A.
      return makeFinding(
        'recepisse_da_manquant_parcours_a',
        'error',
        "Parcours A déclenché (vendeur professionnel sur la cession, titulaire particulier au C.1 de la carte grise) mais aucun récépissé de déclaration d'achat n'a été déposé. Cette pièce est obligatoire pour la soumission ANTS. → Déposez le récépissé délivré par l'ANTS au professionnel : les autres pièces du dossier n'ont rien à corriger.",
        undefined,
        ['recepisse_da'],
      );
    }
    return null;
  },
};

// 26-bis. MiSIV : cross-check des champs VIN / marque / dateMEC issus
// de l'OCR carte_grise avec les données SIV officielles renvoyées par
// /api/misiv/ecg à l'étape 1. MiSIV est la source de vérité absolue —
// un mismatch trahit soit une erreur OCR, soit la dépose d'une CG qui
// n'appartient pas au véhicule recherché.
const vinMisivMismatch: CrossCheckRule = {
  id: 'vin_misiv_mismatch',
  description: 'Le VIN extrait de la carte grise doit correspondre à celui retourné par MiSIV',
  severity: 'error',
  check: (ctx) => {
    if (isImmatConfidenceTooLow(ctx)) return null; // cf. garde de cascade
    // ⚙ Environnement dev/staging : le backend marque is_stub dans la réponse
    // MiSIV quand il a substitué la plaque par un stub (BL-726-DJ). Pas de
    // confrontation VIN possible dans ce contexte → on remonte un INFO clair.
    if (isMisivStubResponse(ctx)) {
      return makeFinding(
        'vin_misiv_mismatch',
        'info',
        `⚙ Environnement non-prod (dev / staging) — la confrontation VIN MiSIV est suspendue. Le backend (misivController.resolveMisivRegPlate) substitue toutes les plaques par un stub fixe (BL-726-DJ) pour ne pas solliciter le TMS dev avec des plaques de test : MiSIV renvoie donc TOUJOURS la même réponse (VIN ${ctx.taxData?.vehicleDetails?.VIN ?? 'inconnu'}), quel que soit le véhicule réellement uploadé. C'est NORMAL en dev et ce n'est PAS une anomalie du dossier. Cette règle redeviendra active automatiquement en prod (vraie plaque interrogée).`,
        ['vin'],
        ['carte_grise'],
      );
    }
    const misivVin = ctx.taxData?.vehicleDetails?.VIN;
    if (!misivVin) return null;
    const cgVin = getFieldValue<string>(ctx.docs.carte_grise, 'vin');
    if (!cgVin) return null;
    const cgNorm = normalizeForCompare(cgVin);
    const misivNorm = normalizeForCompare(misivVin);
    if (cgNorm === misivNorm) return null;

    // Diagnostic de plausibilité : si les VIN ont des WMI (3 premiers
    // caractères = identifiant constructeur mondial) DIFFÉRENTS, il ne
    // s'agit pas du même modèle de véhicule. Confronté à une CG lisible et
    // cohérente avec toutes les autres pièces du dossier, c'est le signe
    // d'une MAUVAISE REQUÊTE MiSIV (immatriculation interrogée erronée à
    // l'étape 1) bien plus probablement qu'un faux document. On rétrograde
    // en info avec ce diagnostic, plutôt que d'émettre une fausse majeure
    // qui fait re-uploader une CG correcte.
    // Cas réel DC-39686 : CG Opel "W0V..." vs MiSIV "VF3..." (Peugeot).
    const cgWmi = cgNorm.slice(0, 3);
    const misivWmi = misivNorm.slice(0, 3);
    const cgVinLooksValid = VIN_REGEX.test(cgNorm);
    const vinCgCoherentAvecCession =
      normalizeForCompare(getFieldValue<string>(ctx.docs.cerfa_cession, 'vin') ?? '') === cgNorm;

    if (cgVinLooksValid && cgWmi !== misivWmi && vinCgCoherentAvecCession) {
      return makeFinding(
        'vin_misiv_mismatch',
        'info',
        `Le VIN de la carte grise (${cgVin}) et celui retourné par MiSIV (${misivVin}) correspondent à des constructeurs différents (WMI ${cgWmi} ≠ ${misivWmi}). Le VIN de la carte grise est cohérent avec le reste du dossier — il s'agit très probablement d'une erreur d'immatriculation interrogée côté MiSIV, et non d'une anomalie du dossier. Vérifier la plaque saisie à l'étape 1.`,
        ['vin'],
        ['carte_grise'],
      );
    }

    // Sinon : divergence réelle à investiguer (même constructeur, écart de
    // quelques caractères → possible erreur OCR ou mauvaise CG).
    return makeFinding(
      'vin_misiv_mismatch',
      'error',
      `Le VIN lu sur la carte grise (${cgVin}) ne correspond pas à celui du fichier SIV (${misivVin}). → Vérifiez que la carte grise déposée est bien celle du véhicule recherché (et que la plaque saisie est correcte) avant l'envoi.`,
      ['vin'],
      ['carte_grise'],
    );
  },
};

const mecMisivMismatch: CrossCheckRule = {
  id: 'mec_misiv_mismatch',
  description: 'La date de 1ère MEC OCR doit correspondre à celle retournée par MiSIV',
  severity: 'warning',
  check: (ctx) => {
    if (isImmatConfidenceTooLow(ctx)) return null; // cf. garde de cascade
    const misivMec = ctx.taxData?.vehicleDetails?.dateMEC;
    if (!misivMec) return null;
    // Si la réponse MiSIV est un stub dev, la MEC est tout aussi douteuse
    // → on ne lève rien (la garde vin_misiv_mismatch porte déjà l'INFO env).
    if (isMisivStubResponse(ctx)) return null;
    const cgMecRaw = getFieldValue<string>(ctx.docs.carte_grise, 'date_premiere_mec');
    const cgMec = parseDateMaybe(cgMecRaw);
    const misivDate = parseDateMaybe(misivMec);
    if (!cgMec || !misivDate) return null;
    if (cgMec.getTime() === misivDate.getTime()) return null;

    // Même garde que vin_misiv_mismatch : si le VIN du dossier ne correspond
    // déjà pas au constructeur MiSIV, la MEC MiSIV porte sur un autre
    // véhicule — ne pas ajouter une 2e alerte trompeuse, rétrograder en info.
    const misivVin = normalizeForCompare(ctx.taxData?.vehicleDetails?.VIN ?? '');
    const cgVin = normalizeForCompare(getFieldValue<string>(ctx.docs.carte_grise, 'vin') ?? '');
    if (misivVin && cgVin && misivVin.slice(0, 3) !== cgVin.slice(0, 3)) {
      return makeFinding(
        'mec_misiv_mismatch',
        'info',
        `Date de 1ère MEC OCR (${cgMecRaw}) ≠ MiSIV (${misivMec}), mais les données MiSIV semblent porter sur un autre véhicule (VIN différent). À traiter avec l'alerte VIN/MiSIV, pas comme une anomalie de la carte grise.`,
        ['date_premiere_mec'],
        ['carte_grise'],
      );
    }
    return makeFinding(
      'mec_misiv_mismatch',
      'warning',
      `La date de 1ère mise en circulation lue sur la carte grise (${cgMecRaw}) diffère de celle du fichier SIV (${misivMec}). → Relisez le champ B de la carte grise et confirmez la bonne date avant l'envoi.`,
      ['date_premiere_mec'],
      ['carte_grise'],
    );
  },
};

// 26. Cohérence date de cession ↔ date d'achat de la demande d'immat.
// Si les deux dates sont remplies, elles doivent matcher (à ±1 jour près).
// Contre-vérification de plausibilité : une date dans le FUTUR est
// matériellement impossible sur un Cerfa déjà signé — c'est une erreur de
// lecture OCR d'un chiffre (6 lu 8 → 2026 lu 2028), pas une incohérence
// documentaire. Dans ce cas on informe (la date plausible fait foi) au
// lieu d'alerter.
const dateCerfaCoherente: CrossCheckRule = {
  id: 'date_cerfa_coherente',
  description: "La date d'achat sur la demande d'immat doit correspondre à la date de cession",
  // MAJEURE (alignement référentiel client / Claude Chat) : une « date d'achat »
  // du Cerfa 13750 différente de la date de cession est un motif de rejet ANTS
  // à corriger avant l'envoi. Le chemin INFO ci-dessous (date dans le futur =
  // confusion OCR sur l'année) reste lui en INFO, la date plausible faisant foi.
  severity: 'error',
  check: (ctx) => {
    const dateVente = parseDateMaybe(getFieldValue<string>(ctx.docs.cerfa_cession, 'date_vente'));
    const dateAchat = parseDateMaybe(getFieldValue<string>(ctx.docs.cerfa_demande_immat, 'date_achat'));
    if (!dateVente || !dateAchat) return null;
    const diffDays = Math.abs(dateAchat.getTime() - dateVente.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays <= 1) return null;

    // Garde §13.7 — colonnes décalées sur l'en-tête VÉHICULE du 13750. Les 4
    // champs sont serrés sur une même ligne : (A) immatriculation | « Date
    // d'achat, le cas échéant » (souvent laissée VIDE) | (I) date du certificat
    // actuel | (B) date de 1ère immatriculation. L'OCR reporte fréquemment la
    // valeur d'une colonne voisine dans la case « date d'achat » vide. Si la
    // « date d'achat » lue coïncide (±1 j) avec la date d'ÉDITION de la carte
    // grise (champ I = date_emission_cg) ou la 1ère MEC (champ B), ce n'est PAS
    // une date d'achat : on ne la croise donc PAS avec la cession (skill §13.7 :
    // « ne JAMAIS croiser le champ I avec la date de cession »). La carte grise
    // fait foi. Cas DE-107-LA (DC-39153) : « date d'achat » vide, OCR a aligné
    // le champ I 06/11/2025 sur date_achat = date_emission_cg de la CG.
    const isNeighborColumnDate = (['date_emission_cg', 'date_premiere_mec'] as const).some((field) => {
      const neighbor = parseDateMaybe(getFieldValue<string>(ctx.docs.carte_grise, field));
      if (!neighbor) return false;
      return Math.abs(dateAchat.getTime() - neighbor.getTime()) / (1000 * 60 * 60 * 24) <= 1;
    });
    if (isNeighborColumnDate) return null;

    const iso = (d: Date): string => d.toISOString().slice(0, 10);
    const tomorrow = ctx.now.getTime() + 24 * 60 * 60 * 1000;
    const achatInFuture = dateAchat.getTime() > tomorrow;
    const venteInFuture = dateVente.getTime() > tomorrow;

    if (achatInFuture !== venteInFuture) {
      const [suspectDate, fiableDate, suspectLabel, fiableLabel] = achatInFuture
        ? [dateAchat, dateVente, "la demande d'immatriculation", 'la cession']
        : [dateVente, dateAchat, 'la cession', "la demande d'immatriculation"];
      const sameDayMonth =
        suspectDate.getUTCDate() === fiableDate.getUTCDate() && suspectDate.getUTCMonth() === fiableDate.getUTCMonth();
      return makeFinding(
        'date_cerfa_coherente',
        'info',
        `Date lue sur ${suspectLabel} (${iso(suspectDate)}) située dans le futur — lecture OCR probablement erronée${
          sameDayMonth ? " (seule l'année diffère, chiffre confondu)" : ''
        }. La date de ${fiableLabel} (${iso(fiableDate)}) fait foi.`,
        undefined,
        ['cerfa_cession', 'cerfa_demande_immat'],
      );
    }

    // Garde de confiance (patch DC-39462 A.4) : le prompt 13750 abaisse à
    // 0.40 la confiance des dates suspectes (année future, mois > 12) —
    // sous 0.85 on ne déclenche pas d'alerte de cohérence sur une lecture
    // douteuse.
    const achatConfidence = getFieldConfidence(ctx.docs.cerfa_demande_immat, 'date_achat');
    if (achatConfidence !== null && achatConfidence < 0.85) return null;

    return makeFinding(
      'date_cerfa_coherente',
      'error',
      `La « date d'achat » de la demande d'immatriculation (${iso(dateAchat)}) ne correspond pas à la date de vente du certificat de cession (${iso(dateVente)}). Le plus souvent, c'est la date de l'ancienne carte grise recopiée par erreur (ou une confusion de champ à la lecture). → Corrigez la « date d'achat » du Cerfa 13750 pour qu'elle soit la date réelle de la vente : tel quel, l'ANTS rejettera le dossier.`,
      undefined,
      ['cerfa_cession', 'cerfa_demande_immat'],
    );
  },
};

/**
 * Détection « pièce d'un autre véhicule ». Quand une pièce déposée porte une
 * immatriculation ET un VIN franchement différents de la carte grise du
 * dossier, ce n'est pas une erreur OCR : c'est un fichier appartenant à un
 * AUTRE dossier, glissé par erreur dans le ZIP (cas DC-40000 : mandat BMW
 * EH-668-NM dans un dossier Opel GN-079-RQ). On le signale explicitement —
 * en warning, car il faut retirer/remplacer la pièce avant soumission — au
 * lieu de laisser coherence_immat ou mandat_operation_type_vide produire des
 * faux positifs trompeurs (« probable erreur OCR »).
 */
const pieceAutreVehicule: CrossCheckRule = {
  id: 'piece_autre_vehicule',
  description: 'Une pièce déposée concerne un autre véhicule que celui du dossier',
  severity: 'warning',
  check: (ctx) => {
    // On ne compare qu'aux pièces porteuses d'immatriculation hors carte grise
    // (la CG est la référence). Une seule alerte agrégée si plusieurs pièces.
    const cgImmat = getFieldValue<string>(ctx.docs.carte_grise, 'immatriculation');
    if (!cgImmat) return null;
    const offenders: string[] = [];
    for (const { docType, label } of IMMAT_DOCS) {
      if (docType === 'carte_grise') continue;
      const doc = ctx.docs[docType];
      if (!doc) continue;
      if (isPieceForAnotherVehicle(ctx, doc)) {
        const docImmat = getFieldValue<string>(doc, 'immatriculation') ?? '?';
        offenders.push(`${label} (${docImmat})`);
      }
    }
    if (offenders.length === 0) return null;
    return makeFinding(
      'piece_autre_vehicule',
      'warning',
      `${offenders.join(', ')} concerne${offenders.length > 1 ? 'nt' : ''} un autre véhicule que la carte grise du dossier (${cgImmat}). Pièce probablement déposée par erreur — la retirer ou la remplacer par la bonne avant soumission ANTS.`,
      ['immatriculation'],
      undefined,
    );
  },
};

// 28-ter. Case d'opération du Cerfa 13750. En CTVO Skipcar, l'acquéreur est
// TOUJOURS un nouveau propriétaire → la case doit être « certificat / changement
// de titulaire ». Si la case lue est « changement d'état civil », « changement
// d'adresse », « correction », « duplicata »…, l'ANTS traite la demande comme une
// simple mise à jour de l'ANCIEN titulaire et NON comme un changement de
// propriétaire → rejet (cas DC-39240 : « changement_etat_civil » sur un dossier
// LECLERCQ → ERYIKIT). Confidence-gated : une croix parmi des cases empilées est
// faillible à l'OCR → MAJEURE si lue avec certitude, sinon point d'attention.
const CERFA_CASE_LABELS: Record<string, string> = {
  duplicata: 'Duplicata',
  correction: "Correction d'erreur",
  changement_domicile: "Changement d'adresse",
  changement_etat_civil: "Changement d'état civil ou matrimonial",
  changement_caracteristiques: 'Changement de caractéristiques techniques',
};
const CERFA_CASE_CONFIDENCE_THRESHOLD = 0.85;
const cerfaCaseIncorrecte: CrossCheckRule = {
  id: 'cerfa_case_incorrecte',
  description: 'La case du Cerfa 13750 doit correspondre à un changement de titulaire',
  severity: 'error',
  check: (ctx) => {
    const demande = ctx.docs.cerfa_demande_immat;
    if (!demande) return null;
    const caseCochee = getFieldValue<string>(demande, 'case_cochee');
    // "certificat" = bon choix ; "unknown" = case non lue (trou OCR) ; "aucune" =
    // pas de case cochée → géré par cerfa13750CaseOperationManquante. Cette règle
    // ne traite QUE le cas d'une MAUVAISE case effectivement cochée.
    if (!caseCochee || caseCochee === 'certificat' || caseCochee === 'unknown' || caseCochee === 'aucune') return null;
    // On fait CONFIANCE à la lecture OCR de la case (le prompt cerfa_demande_immat
    // encode la règle spatiale exacte : le carré coché est immédiatement à GAUCHE
    // de son libellé, ex carré marqué + « Certificat » = certificat). Pas de
    // devinette ni de recoupement : on reflète simplement la case lue, gradué par
    // la confiance.
    const label = CERFA_CASE_LABELS[caseCochee] ?? caseCochee;
    const confidence = getFieldConfidence(demande, 'case_cochee') ?? 0;

    // Lecture incertaine (< 0.85) : simple relecture.
    if (confidence < CERFA_CASE_CONFIDENCE_THRESHOLD) {
      return makeFinding(
        'cerfa_case_incorrecte',
        'warning',
        `La case d'opération de la demande d'immatriculation (Cerfa 13750) ressort comme « ${label} » (lecture incertaine). → Vérifiez visuellement que c'est bien « changement de titulaire / demande de certificat » qui est cochée : une mauvaise case fait rejeter le dossier par l'ANTS.`,
        ['case_cochee'],
        ['cerfa_demande_immat'],
      );
    }

    return makeFinding(
      'cerfa_case_incorrecte',
      'error',
      `La case cochée sur la demande d'immatriculation (Cerfa 13750) est « ${label} » au lieu de « changement de titulaire / demande de certificat ». → L'ANTS traiterait le dossier comme « ${label.toLowerCase()} » de l'ancien titulaire, et non comme un changement de propriétaire : le dossier serait rejeté. Faites cocher la case « changement de titulaire (acquisition) » avant l'envoi.`,
      ['case_cochee'],
      ['cerfa_demande_immat'],
    );
  },
};

// 28-bis. Cohérence date de cession : la mention manuscrite « Vendu / Cédé le … »
// portée sur la CARTE GRISE par le vendeur doit correspondre à la date de vente
// du certificat de cession. Un écart trahit une CG barrée à une autre date que
// la cession (souvent : CG barrée trop tôt) — à éclaircir avec le vendeur avant
// l'envoi ANTS. Point d'attention (non bloquant), avec gardes OCR (confiance,
// date future = confusion de chiffre). Cas GK/EW : « Vendu le 12/05 » vs
// cession 23/05.
const coherenceDateCessionCgMention: CrossCheckRule = {
  id: 'coherence_date_cession_cg_mention',
  description: 'La mention de cession manuscrite sur la carte grise doit correspondre à la date de cession',
  severity: 'warning',
  check: (ctx) => {
    const cgMention = parseDateMaybe(getFieldValue<string>(ctx.docs.carte_grise, 'date_cession_mention'));
    const dateVente = parseDateMaybe(getFieldValue<string>(ctx.docs.cerfa_cession, 'date_vente'));
    if (!cgMention || !dateVente) return null;

    const diffDays = Math.abs(cgMention.getTime() - dateVente.getTime()) / (1000 * 60 * 60 * 24);
    if (diffDays <= 1) return null;

    // Garde date future = confusion OCR sur un chiffre (6↔8) : on ignore une
    // date matériellement impossible plutôt que d'alerter sur une lecture douteuse.
    const tomorrow = ctx.now.getTime() + 24 * 60 * 60 * 1000;
    if (cgMention.getTime() > tomorrow || dateVente.getTime() > tomorrow) return null;

    const iso = (d: Date): string => d.toISOString().slice(0, 10);

    // Ancrage par l'heure manuscrite. La mention « Vendu le … à HHhMM » porte
    // souvent une heure, tout comme la cession (heure_vente). Si les deux heures
    // coïncident, c'est le MÊME évènement de vente : ce sont alors les chiffres
    // de la DATE manuscrite qui ont été mal lus (5↔1, 3↔1 sur une CG barrée),
    // PAS une carte grise barrée à une autre date. Cas HH-975-PP : CG « 12h52 »
    // ↔ cession 12h51, dates lues 21/01 vs 23/05.
    const parseHeure = (h: string | null): number | null => {
      if (!h || !/^\d{1,2}[:hH]\d{2}$/.test(h)) return null;
      const [hh, mm] = h.split(/[:hH]/).map((n) => parseInt(n, 10));
      return Number.isFinite(hh) && Number.isFinite(mm) ? hh * 60 + mm : null;
    };
    const heureCg = parseHeure(getFieldValue<string>(ctx.docs.carte_grise, 'heure_cession_mention'));
    const heureVente = parseHeure(getFieldValue<string>(ctx.docs.cerfa_cession, 'heure_vente'));
    const heuresConcordent = heureCg !== null && heureVente !== null && Math.abs(heureCg - heureVente) <= 2;

    // Garde confiance : la cession fait foi sur la date de vente (§3-bis) ; la
    // mention « Vendu le » de la CG n'est qu'un recoupement manuscrit. Seuil
    // relevé à 0.92 (aligné MISIV/immat §13.5) — une mention barrée à 0.85
    // restait douteuse et passait pile la garde stricte précédente (< 0.85).
    const mentionConfidence = getFieldConfidence(ctx.docs.carte_grise, 'date_cession_mention');
    const mentionPeuFiable = mentionConfidence !== null && mentionConfidence < 0.92;

    // SILENCE TOTAL (pas même une INFO) dès que l'heure prouve le même
    // évènement OU que la lecture manuscrite est peu fiable : l'écart de date
    // est alors un pur artefact de relecture OCR sur une mention barrée (ex.
    // EB-741-RE : « Vendu le 25/06/26 » lu 2025-06-25, même jour/mois que la
    // cession 2026-06-25 — année mal lue). La cession fait foi (§3-bis) et il
    // n'y a RIEN d'actionnable. Décision Luc : ne JAMAIS faire ressortir cet
    // écart (form-cgia fait foi et n'affiche rien — la review doit être ISO,
    // une INFO ici n'était que du bruit en relecture GOD).
    if (heuresConcordent || mentionPeuFiable) {
      return null;
    }

    // WARNING : mention manuscrite lisible (confiance ≥ 0.92) qui diverge
    // réellement, sans heure concordante → la CG a vraisemblablement été barrée
    // à une autre date que la cession (cas GK/EW). À éclaircir avant l'envoi.
    return makeFinding(
      'coherence_date_cession_cg_mention',
      'warning',
      `La mention manuscrite « Vendu le » de la carte grise (${iso(cgMention)}) ne correspond pas à la date de vente du certificat de cession (${iso(dateVente)}). Ces deux dates devraient être identiques. → À éclaircir avec le vendeur : si la vraie date de vente est celle de la cession, la carte grise a été barrée à une autre date, ce qui peut poser problème côté ANTS.`,
      undefined,
      ['carte_grise', 'cerfa_cession'],
    );
  },
};

// 29. Permis de conduire : la FACE D'IDENTITÉ (recto carte / couverture rose)
// doit être fournie. Le permis est une pièce maîtresse CTVO (§6) : sans la
// face qui porte photo + nom + prénom + date de naissance + n° de permis, on
// ne peut PAS vérifier l'identité de l'acquéreur → document incomplet, non
// validable. Deux pièges fréquents :
//   - format CB : seul le VERSO (catégories + bande MRZ) est déposé ;
//   - ancien rose 3 volets : seules les pages intérieures (cases de validités
//     périodiques) sont déposées, la couverture (identité + photo) manque.
// On s'appuie UNIQUEMENT sur le flag explicite face_identite_presente=false posé
// par le modèle lors de l'extraction du permis. On NE déduit PAS l'absence de
// recto d'un « tous les champs identité vides » : un permis reçu via un fichier
// MULTI-DOCUMENTS (CNI + permis dans le même PDF, warning
// contient_aussi_permis_conduire) est enregistré champs vides alors qu'il est
// bel et bien présent — l'absence de champs ne signifie donc pas absence de
// recto. Cas réel DC-39460 : permis recto+verso dans le PDF de la CNI.
const permisFaceIdentiteManquante: CrossCheckRule = {
  id: 'permis_recto_manquant',
  description: "La face d'identité du permis (recto carte / couverture) doit être fournie",
  // Non bloquant depuis l'arbitrage `piece_presente_dite_manquante` : cf. PIECE_NON_VERIFIEE.
  severity: 'warning',
  check: (ctx) => {
    const permis = ctx.docs.permis_conduire;
    if (!permis) {
      // Routage (personne × face) : un VERSO de permis titulaire est rangé
      // dans son slot dédié sans recto correspondant → la face identité
      // manque physiquement au dossier.
      if (ctx.docs.permis_conduire_verso) {
        return makeFinding(
          'permis_recto_manquant',
          'warning',
          `${PIECE_NON_VERIFIEE}la face du permis qui porte l'identité (photo, nom, prénom, date de naissance, n° de permis) n'a pas été retrouvée : seul le verso (catégories / validités) est reconnu. → Vérifiez la pièce déposée ; si le recto manque réellement, ajoutez-le.`,
          undefined,
          ['permis_conduire_verso'],
        );
      }
      return null;
    }
    const faceFlag = getFieldValue<boolean>(permis, 'face_identite_presente');
    if (faceFlag === false) {
      return makeFinding(
        'permis_recto_manquant',
        'warning',
        `${PIECE_NON_VERIFIEE}la face lue ne montre que les catégories / validités — verso d'un permis carte, ou pages intérieures d'un ancien permis rose. La face d'identité (photo, nom, prénom, date de naissance, n° de permis) n'a pas été retrouvée. → Vérifiez la pièce déposée ; si le recto manque réellement, ajoutez-le.`,
        undefined,
        ['permis_conduire'],
      );
    }
    return null;
  },
};

// 30. Pièce d'identité (acquéreur) : la FACE D'IDENTITÉ avec PHOTO (recto)
// doit être fournie — symétrique du permis. Contrairement au permis, le VERSO
// d'une CNI porte une bande MRZ qui contient le nom/prénom en texte : une CNI
// verso seule N'EST DONC PAS « identité vide ». On s'appuie donc sur le flag
// explicite `face_identite_presente` (présence de la PHOTO) posé par le modèle,
// et non sur l'absence de champs. Vaut pour l'ancienne CNI cartonnée comme pour
// la nouvelle CNI format carte (post-2021).
// Seuil de confiance du flag `face_identite_presente` en dessous duquel on ne
// BLOQUE plus (retour Biwiz 2026-07 : verso CNI redemandé alors que la pièce
// recto+verso était bien déposée). Aligné sur les autres gardes du fichier
// (CASE_ASSURANCE / justif domicile = 0.85). Règle maîtresse du référentiel
// CTVO : « donnée mal lue par l'OCR ≠ donnée manquante » → relecture, pas blocage.
const FACE_IDENTITE_CONFIDENCE_THRESHOLD = 0.85;

const pieceIdentiteFaceManquante: CrossCheckRule = {
  id: 'piece_identite_recto_manquant',
  description: "La face d'identité (recto / côté photo) de la pièce d'identité doit être fournie",
  // Non bloquant depuis l'arbitrage `piece_presente_dite_manquante` : cf. PIECE_NON_VERIFIEE.
  severity: 'warning',
  check: (ctx) => {
    // Routage (personne × face) : un VERSO de CNI titulaire rangé dans son
    // slot dédié sans aucun recto (ni titulaire ni hébergeur) → la face photo
    // manque physiquement au dossier.
    if (!ctx.docs.piece_identite && !ctx.docs.piece_identite_hebergeur && ctx.docs.piece_identite_verso) {
      return makeFinding(
        'piece_identite_recto_manquant',
        'warning',
        `${PIECE_NON_VERIFIEE}seule la face verso (bande MRZ + adresse) de la pièce d'identité de l'acquéreur est reconnue, le recto (côté photo) n'a pas été retrouvé. → Vérifiez la pièce déposée ; si le recto manque réellement, ajoutez-le — c'est lui qui prouve l'identité de l'acquéreur.`,
        undefined,
        ['piece_identite_verso'],
      );
    }
    // On évalue la CNI de l'ACQUÉREUR (pas le slot brut) : en mode hébergement,
    // le dossier contient 2 CNI (acquéreur + hébergeur) et l'auto-extract a pu
    // ranger le recto de l'acquéreur dans le slot hébergeur — pickAcquereurPi le
    // resélectionne par croisement du nom/prénom avec la cession. Sans ça, on
    // alertait « recto manquant » alors que le recto de l'acquéreur EST dans le
    // dossier, simplement mal slotté (cas HH-975-PP).
    const picked = pickAcquereurPi(ctx);
    if (!picked) return null;
    // Le recto est présent dès qu'UNE des faces de la CNI acquéreur porte
    // l'identité : on ne déclenche que si la face retenue est explicitement le
    // verso (face_identite_presente === false) ET qu'aucune autre pièce
    // d'identité du dossier ne fournit la face photo de l'acquéreur.
    const pickedHasFace = getFieldValue<boolean>(picked.pi, 'face_identite_presente') !== false;
    if (pickedHasFace) return null;
    // Filet : une autre CNI déposée (recto/verso en 2 fichiers) porte-t-elle la
    // face d'identité ? Si oui, le recto de l'acquéreur est physiquement là.
    const otherPi = picked.slot === 'piece_identite' ? ctx.docs.piece_identite_hebergeur : ctx.docs.piece_identite;
    if (getFieldValue<boolean>(otherPi, 'face_identite_presente') === true) return null;
    // Garde CONFIANCE (retour Biwiz 2026-07) : ne BLOQUER que si le modèle est
    // CONFIANT que la face retenue est le verso seul. Un flag
    // `face_identite_presente=false` peu fiable (CNI recto+verso pourtant
    // déposée, OCR hésitant) provoquait un faux « déposez le recto » qui
    // bloquait l'agence. Sous le seuil, on rétrograde en RELECTURE non bloquante
    // plutôt que d'empêcher la soumission.
    const faceConf = getFieldConfidence(picked.pi, 'face_identite_presente');
    if (faceConf !== null && faceConf < FACE_IDENTITE_CONFIDENCE_THRESHOLD) {
      return makeFinding(
        'piece_identite_recto_manquant',
        'warning',
        "Le recto (côté photo) de la pièce d'identité de l'acquéreur n'a pas été détecté avec certitude. → Vérifiez qu'il est bien joint (photo, nom, prénom, date de naissance) ; s'il est déjà présent, vous pouvez ignorer ce message.",
        undefined,
        [picked.slot],
      );
    }
    return makeFinding(
      'piece_identite_recto_manquant',
      'warning',
      `${PIECE_NON_VERIFIEE}la face lue ne montre que le verso (bande MRZ + adresse), sans photo : le recto de la pièce d'identité de l'acquéreur n'a pas été retrouvé. → Vérifiez la pièce déposée ; si le recto manque réellement, ajoutez-le — c'est lui qui prouve l'identité de l'acquéreur.`,
      undefined,
      [picked.slot],
    );
  },
};

// 30-bis. Un FICHIER de pièce d'identité qui contient AUSSI la pièce d'une
// AUTRE personne (ex : les CNI des deux membres d'un couple scannées ensemble).
// Le pipeline n'extrait que la pièce PRINCIPALE du fichier : la seconde
// personne n'existe nulle part dans le dossier (warning
// `contient_aussi_piece_identite` posé par le prompt PI, retours François
// 2026-07-29). On la réclame en fichier séparé — point d'attention, pas un
// blocage : la pièce principale reste exploitable. Ne vise QUE les slots PI :
// le même warning porté par un PERMIS (fichier CNI + permis, cas §13.13 du
// référentiel) est déjà couvert par le placeholder multi-doc.
const PI_SLOTS_MULTI_FICHIER: readonly CgDocType[] = [
  'piece_identite',
  'piece_identite_verso',
  'piece_identite_cotitulaire',
  'piece_identite_cotitulaire_verso',
  'piece_identite_hebergeur',
  'piece_identite_hebergeur_verso',
];

const pieceIdentiteMultipleMemeFichier: CrossCheckRule = {
  id: 'piece_identite_multiple_meme_fichier',
  description: "Un fichier de pièce d'identité contient la pièce d'une autre personne, non extraite",
  severity: 'warning',
  check: (ctx) => {
    const slotConcerne = PI_SLOTS_MULTI_FICHIER.find((slot) => {
      const doc = ctx.docs[slot];
      return Array.isArray(doc?.ocrResponse?.warnings) && doc.ocrResponse.warnings.includes('contient_aussi_piece_identite');
    });
    if (!slotConcerne) return null;
    return makeFinding(
      'piece_identite_multiple_meme_fichier',
      'warning',
      "Un des fichiers de pièce d'identité contient AUSSI la pièce d'une autre personne (ex : les deux CNI d'un couple scannées ensemble). Seule la première a été analysée. → Déposez chaque pièce d'identité dans un fichier séparé (recto et verso), pour que chacune soit vérifiée et rattachée à la bonne personne.",
      undefined,
      [slotConcerne],
    );
  },
};

// 30. Validité des pièces d'identité (acquéreur + hébergeur). Une PI FAIT FOI
// (§ règle maîtresse) ; expirée = inexploitable → MAJEURE. En mode hébergement,
// une CNI hébergeur périmée invalide le pack hébergement.
//
// ⚠ GARDE LÉGALE FRANÇAISE (décret 2013-1188) : les CNI plastifiées délivrées
// entre 2004 et 2013 à un MAJEUR ont leur validité prolongée de +5 ans (15 ans
// au lieu de 10) pour l'usage administratif domestique (ANTS, hébergement). La
// date imprimée est donc dépassée de ~5 ans alors que la pièce reste VALIDE. On
// ajoute +5 ans à l'expiration imprimée des CNI (borne la plus généreuse) AVANT
// de conclure : si même +5 ans c'est passé, la pièce est indiscutablement
// périmée ; sinon on ne signale pas. Passeport / titre de séjour : pas de
// prolongation. Cas FH-451-ZR : CNI hébergeur MIALON imprimée 27/05/2023,
// délivrée 2013 → réellement valable 27/05/2028 → PAS de fausse alerte.
const PI_EXPIRY_CONFIDENCE_THRESHOLD = 0.85;
const PI_VALIDITY_SLOTS: {
  slot: CgDocType;
  label: string;
}[] = [
  { slot: 'piece_identite', label: "La pièce d'identité de l'acquéreur" },
  { slot: 'piece_identite_hebergeur', label: "La pièce d'identité de l'hébergeur" },
  // Le co-titulaire est titulaire au même titre que l'acquéreur : une pièce
  // expirée de sa part fait rejeter le dossier par l'ANTS exactement pareil.
  { slot: 'piece_identite_cotitulaire', label: "La pièce d'identité du co-titulaire" },
  // Faces VERSO (routage personne × face) : la date d'expiration est parfois
  // portée par le verso (MRZ) — même sanction ANTS si périmée.
  { slot: 'piece_identite_verso', label: "La pièce d'identité de l'acquéreur" },
  { slot: 'piece_identite_hebergeur_verso', label: "La pièce d'identité de l'hébergeur" },
  { slot: 'piece_identite_cotitulaire_verso', label: "La pièce d'identité du co-titulaire" },
];
const addYears = (d: Date, n: number): Date => {
  const r = new Date(d.getTime());
  r.setFullYear(r.getFullYear() + n);
  return r;
};
const pieceIdentiteExpiree: CrossCheckRule = {
  id: 'piece_identite_expiree',
  description: "Une pièce d'identité (acquéreur ou hébergeur) ne doit pas être expirée",
  severity: 'error',
  check: (ctx) => {
    for (const { slot, label } of PI_VALIDITY_SLOTS) {
      const pi = ctx.docs[slot];
      if (!pi) continue;
      // GF-553-KD (retour du 19/07) : « tu soulignes la pièce d'identité
      // bloquante, elle est valable jusqu'au 14/01/2033 ». Le dossier portait
      // DEUX pièces d'identité — l'une expirée en 2015, l'autre valable
      // jusqu'en 2033 — et la règle bloquait sur la première venue, sans
      // vérifier qu'elle était bien celle de l'acquéreur. On ne juge la
      // validité du slot acquéreur que si le croisement avec la cession l'y
      // désigne : une pièce expirée qui n'est celle de personne relève de
      // `piece_identite_etrangere_au_dossier`, pas d'un blocage de validité.
      if (slot === 'piece_identite') {
        const picked = pickAcquereurPi(ctx);
        if (picked && picked.pi !== pi) continue;
      }
      const printedExpiry = parseDateMaybe(getFieldValue<string>(pi, 'date_expiration'));
      // Pas de date d'expiration lisible → format ancien / illisible : on ne
      // conclut pas à une péremption (cf. permis rose §6, donnée mal lue ≠ manquante).
      if (!printedExpiry) continue;
      const conf = getFieldConfidence(pi, 'date_expiration');
      if (conf !== null && conf < PI_EXPIRY_CONFIDENCE_THRESHOLD) continue;

      const typeDoc = (getFieldValue<string>(pi, 'type_document') ?? '').toLowerCase();
      const isPasseportOuTitre =
        typeDoc.includes('passeport') ||
        typeDoc.includes('sejour') ||
        typeDoc.includes('séjour') ||
        typeDoc.includes('titre');
      const effectiveExpiry = isPasseportOuTitre ? printedExpiry : addYears(printedExpiry, 5);
      if (effectiveExpiry.getTime() >= ctx.now.getTime()) continue; // encore valide (même avec +5)

      const iso = (d: Date): string => d.toISOString().slice(0, 10);
      return makeFinding(
        'piece_identite_expiree',
        'error',
        `${label} est expirée (valable jusqu'au ${iso(effectiveExpiry)}). Une pièce d'identité périmée invalide le dossier${slot === 'piece_identite_hebergeur' ? ' (pack hébergement)' : ''} : fournir une pièce d'identité en cours de validité (CNI renouvelée, passeport ou titre de séjour valide).`,
        ['date_expiration'],
        [slot],
      );
    }
    return null;
  },
};

// 28-quater. Case d'opération en haut du Cerfa 13750. Pour un changement de
// propriétaire, c'est la case « Certificat » qui doit normalement être cochée —
// MAIS son ABSENCE n'est PAS bloquante : l'ANTS déduit qu'il s'agit d'un
// changement de titulaire à partir du certificat de cession joint. Une case vide
// ne fait donc PAS rejeter le dépôt → on n'émet qu'une INFO de relecture
// (diagnostic god, masquée côté agence), jamais une MAJEURE ni une MINEURE.
//
// ⚠ NE PAS confondre avec une MAUVAISE case cochée (« Duplicata »,
// « Correction d'erreur »…) : celle-là CONTREDIT activement l'objet du dossier et
// fait échouer le dépôt → elle RESTE une MAJEURE, gérée par cerfaCaseIncorrecte.
//
// Distinction OCR (module seulement le wording / la sévérité de relecture) :
//  - "aucune" = les 6 carrés sont vus vides → INFO : l'ANTS déduit de la cession.
//  - "unknown"/null = zone non lue (trou OCR) → relecture MINEURE : sous une zone
//    illisible peut se cacher une MAUVAISE case (qui, elle, bloquerait) → contrôle
//    visuel invité, sans bloquer.
// Cas FZ-361-QS (DC-39155) et FH-451-ZR (DC-40245) : 13750 sans case cochée,
// dossiers parfaitement valides.
const cerfa13750CaseOperationManquante: CrossCheckRule = {
  id: 'cerfa_13750_case_operation_manquante',
  description: "L'absence de case d'opération sur le Cerfa 13750 n'est pas bloquante (l'ANTS déduit de la cession)",
  severity: 'warning',
  check: (ctx) => {
    const demande = ctx.docs.cerfa_demande_immat;
    if (!demande) return null;
    const caseCochee = getFieldValue<string>(demande, 'case_cochee');

    // Cas 1 — l'OCR confirme que les cases sont VIDES. Non bloquant : l'ANTS
    // déduit la nature de l'opération (changement de titulaire) du certificat de
    // cession joint. Simple INFO, quelle que soit la confiance — jamais MINEURE.
    if (caseCochee === 'aucune') {
      return makeFinding(
        'cerfa_13750_case_operation_manquante',
        'info',
        "Aucune case d'opération n'est cochée en haut du Cerfa 13750. Ce n'est pas bloquant : l'ANTS déduit qu'il s'agit d'un changement de titulaire à partir du certificat de cession joint. Pour un dossier impeccable, faire cocher la case « Certificat ».",
        ['case_cochee'],
        ['cerfa_demande_immat'],
      );
    }

    // Cas 2 — l'OCR n'a pas pu lire la zone (trou OCR). Relecture MINEURE : une
    // case vide ne bloque pas, mais sous une zone illisible peut se cacher une
    // MAUVAISE case cochée (« Duplicata »…) qui, elle, ferait rejeter le dépôt.
    if (!caseCochee || caseCochee === 'unknown') {
      return makeFinding(
        'cerfa_13750_case_operation_manquante',
        'warning',
        "La case d'opération du Cerfa 13750 n'a pas pu être lue. → Vérifiez-la visuellement : une case vide n'est pas bloquante (l'ANTS déduit de la cession), mais une MAUVAISE case (« Duplicata », « Correction »…) ferait rejeter la demande.",
        ['case_cochee'],
        ['cerfa_demande_immat'],
      );
    }

    // Une case connue est cochée → cerfa_case_incorrecte juge si c'est la bonne.
    return null;
  },
};

// Incohérence interne d'un document (anti-fraude / suspicion de faux). Le modèle
// OCR signale une contradiction interne d'une pièce via warnings
// ["incoherence_document"] + le champ `incoherences_document` (liste de
// descriptions courtes) — ex. adresse RUE vs AVENUE sur le MÊME JDC. MINEURE et
// Dieu-only (cf. GOD_ONLY_RULE_IDS dans findingsDisplay) : on n'affiche pas
// "suspicion de faux" à l'agence, seul le pôle god le voit.
const incoherenceDocument: CrossCheckRule = {
  id: 'incoherence_document',
  description: "Un document ne doit pas contenir d'informations internes contradictoires",
  severity: 'warning',
  check: (ctx) => {
    const details: string[] = [];
    const affected: string[] = [];
    for (const [docType, doc] of Object.entries(ctx.docs)) {
      if (!doc) continue;
      const raw = getFieldValue<unknown>(doc, 'incoherences_document');
      const list = Array.isArray(raw)
        ? raw.filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
        : [];
      const warned =
        Array.isArray(doc.ocrResponse?.warnings) && doc.ocrResponse.warnings.includes('incoherence_document');
      if (list.length === 0 && !warned) continue;
      affected.push(docType);
      if (list.length > 0) list.forEach((d) => details.push(`${docType} : ${d}`));
      else details.push(`${docType} : incohérence interne détectée`);
    }
    if (details.length === 0) return null;
    return makeFinding(
      'incoherence_document',
      'warning',
      `Incohérence dans le document (suspicion de faux) — ${details.join(' ; ')}. À vérifier sur l'original.`,
      undefined,
      affected as CgDocType[],
    );
  },
};

// SIRET vendeur (cession) tronqué au SIREN. Après le fix du prompt (extraction à
// 14 chiffres), une valeur à 9 chiffres = le document ne porte réellement qu'un
// SIREN → MAJEURE (décision François 2026-07 : la cession doit porter le SIRET
// complet à 14 chiffres pour un vendeur personne morale).
const cessionVendeurSirenAuLieuDeSiret: CrossCheckRule = {
  id: 'cession_vendeur_siren_au_lieu_de_siret',
  description: 'Le SIRET vendeur de la cession doit comporter 14 chiffres (pas seulement le SIREN à 9)',
  severity: 'error',
  check: (ctx) => {
    const cession = ctx.docs.cerfa_cession;
    if (!cession) return null;
    const raw = getFieldValue<string>(cession, 'vendeur_siret');
    if (!raw) return null;
    const digits = String(raw).replace(/\D/g, '');
    if (digits.length !== 9) return null; // 14 = OK ; autres longueurs = OCR partiel → on ne bloque pas ici
    return makeFinding(
      'cession_vendeur_siren_au_lieu_de_siret',
      'error',
      "Le certificat de cession ne porte qu'un SIREN (9 chiffres) au lieu du SIRET complet (14 chiffres) pour le vendeur professionnel. → Compléter avec le SIRET à 14 chiffres.",
      ['vendeur_siret'],
      ['cerfa_cession'],
    );
  },
};

// N° de formule absent ou illisible sur la carte grise → le coupon détachable
// (où il figure) a probablement été retiré → demander le VERSO de la CG. MINEURE.
//
// On se base sur la VALEUR du champ (absente OU hors format SIV "20xx…"), PAS sur
// le warning OCR `numero_formule_coupon_detache` : ce warning n'est émis que si CE
// scan précis a détecté le coupon détaché — non fiable sur un doublon de CG, ou
// quand le modèle "invente" un numéro sur une autre zone (cas GH-828-XX :
// "F2AX28QT2HZAA0518"). Baser la règle sur la valeur la rend déterministe : que le
// n° soit null ou aberrant, il échoue au format SIV → l'alerte part dans tous les cas.
const numeroFormuleCouponDetache: CrossCheckRule = {
  id: 'numero_formule_coupon_detache',
  description: 'N° de formule absent ou illisible sur la CG (coupon détachable retiré) → demander le verso',
  severity: 'warning',
  check: (ctx) => {
    const cg = ctx.docs.carte_grise;
    if (!cg) return null;
    // Ancienne CG (format papier rose, pré-SIV 2009) : pas de n° de formule par
    // nature — géré par la règle `ancienne_cg_papier_rose`, pas de coupon à réclamer.
    if (getFieldValue<boolean>(cg, 'ancien_format') === true) return null;
    const raw = getFieldValue<string>(cg, 'numero_formule');
    const normalized = (raw ?? '').replace(/\s+/g, '').toUpperCase();
    // N° de formule valide (format SIV) présent → rien à signaler.
    if (FORMULE_FORMAT.test(normalized)) return null;
    return makeFinding(
      'numero_formule_coupon_detache',
      'warning',
      "Le numéro de formule est absent ou illisible sur la carte grise (coupon détachable retiré ?). → Demander à l'agence le VERSO de la carte grise, où le numéro figure.",
      ['numero_formule'],
      ['carte_grise'],
    );
  },
};

// 35. Cohérence de l'ADRESSE de l'acquéreur entre le justificatif de domicile
// et le Cerfa 13750 (demande d'immatriculation).
//
// Retour François 2026-07-20 (EQ-598-TE) : « Adresse différence entre le
// justificatif de domicile et le cerfa demande d'immatriculation qui n'est pas
// remontée ». Aucune règle ne comparait ces deux adresses — une vraie
// divergence de code postal ou de commune passait donc totalement inaperçue,
// alors que c'est l'adresse qui sera immatriculée.
//
// SÉVÉRITÉ : `warning`. Le JDC FAIT FOI pour l'adresse (§5 du référentiel) et
// le 13750 n'est qu'un document de RECOUPEMENT → règle maîtresse §14 : une
// incohérence sur un document de recoupement, alors que la source qui fait foi
// reste exploitable, est MINEURE. On signale pour correction avant dépôt ANTS,
// on ne bloque pas la soumission.
//
// ⚠️ ANTI-FAUX-POSITIF (c'est tout l'enjeu de cette règle) : §5 précise qu'une
// « légère variation de libellé (extension de voie, abréviation, casse) » n'est
// PAS une incohérence. Sur EQ-598-TE précisément, le JDC portait « 19 AV DU MAL
// DE LATTRE DE TASSIGNY » et le Cerfa « 19 Avenue du Maréchal de Lattre de
// Tassigny » : c'est LA MÊME adresse, « MAL » étant l'abréviation postale
// officielle de « maréchal » — l'agence l'a d'ailleurs répondu au pôle CG le
// 21/07. La comparaison passe donc par `compareAddresses`, qui développe les
// abréviations AFNOR avant de conclure. Sans ça, cette règle recréerait
// exactement le faux positif qu'on cherche à éviter.
//
// Retour François 2026-08-21 (GL-911-WF) : « erreur (…) alors qu'on est
// exactement sur la même adresse ». Deux causes cumulées, traitées dans
// `addressNormalize` — le type de voie comptait comme un nom de voie, et la
// garde de transcription ne jouait que sur une divergence isolée.
const coherenceAdresseJdc13750: CrossCheckRule = {
  id: 'coherence_adresse_jdc_13750',
  description: "L'adresse du justificatif de domicile doit correspondre à celle du Cerfa 13750",
  severity: 'warning',
  check: (ctx) => {
    const jdc = ctx.docs.justif_domicile;
    const cerfa = ctx.docs.cerfa_demande_immat;
    if (!jdc || !cerfa) return null;

    // En mode hébergement, l'adresse portée par le justificatif est celle de
    // l'hébergeur : c'est bien elle qui doit être immatriculée, la comparaison
    // reste donc valide et pertinente.
    // `type_voie` et `nom_voie` sont passés SÉPARÉMENT (et non concaténés comme
    // avant) : un « ROUTE » d'un côté et un « Chemin » de l'autre pour la même
    // voie ne doit pas ressortir comme un nom de voie différent. Cf. le commentaire
    // de `compareAddresses` et le cas GL-911-WF.
    const jdcAddress = {
      streetNumber: getFieldValue<string>(jdc, 'numero_voie'),
      streetType: getFieldValue<string>(jdc, 'type_voie'),
      streetLabel: getFieldValue<string>(jdc, 'nom_voie'),
      postalCode: getFieldValue<string>(jdc, 'code_postal'),
      city: getFieldValue<string>(jdc, 'ville'),
    };
    const cerfaAddress = {
      streetNumber: getFieldValue<string>(cerfa, 'numero_voie'),
      streetType: getFieldValue<string>(cerfa, 'type_voie'),
      streetLabel: getFieldValue<string>(cerfa, 'nom_voie'),
      postalCode: getFieldValue<string>(cerfa, 'code_postal'),
      city: getFieldValue<string>(cerfa, 'ville'),
    };

    const divergences = compareAddresses(jdcAddress, cerfaAddress);
    if (divergences.length === 0) return null;

    // GARDE CONFIANCE (même logique que les autres règles du fichier) : une
    // divergence portée par une lecture OCR douteuse est un indice de
    // relecture, pas un constat. On ne veut pas renvoyer l'agence corriger un
    // Cerfa à cause d'un champ mal lu.
    const CONFIDENCE_THRESHOLD = 0.85;
    const confidenceFields: Record<AddressDivergence, string> = {
      code_postal: 'code_postal',
      commune: 'ville',
      numero_voie: 'numero_voie',
      type_voie: 'type_voie',
      nom_voie: 'nom_voie',
    };
    const isLowConfidence = divergences.every((d) => {
      const key = confidenceFields[d];
      const cJdc = getFieldConfidence(jdc, key);
      const cCerfa = getFieldConfidence(cerfa, key);
      return (cJdc !== null && cJdc < CONFIDENCE_THRESHOLD) || (cCerfa !== null && cCerfa < CONFIDENCE_THRESHOLD);
    });

    const labels = divergences.map((d) => DIVERGENCE_LABELS[d]).join(', ');

    // Écart de pure transcription (chaque composant divergent est contredit par
    // un autre qui concorde) → INFO de relecture, jamais une demande de correction.
    // Sans cette garde la règle se déclenchait sur ~40 % du parc, presque
    // toujours sur un code postal manuscrit mal lu alors que la commune
    // confirmait la même adresse (cf. isTranscriptionOnlyDivergence).
    if (isTranscriptionOnlyDivergence(divergences, jdcAddress, cerfaAddress)) {
      return makeFinding(
        'coherence_adresse_jdc_13750',
        'info',
        `Écart sur ${labels} entre le justificatif de domicile et le Cerfa 13750, mais le reste de l'adresse concorde ` +
          "(même commune, même code postal ou même nom de voie) : il s'agit très probablement d'une erreur de recopie " +
          "ou de lecture. → Vérifiez la valeur sur le Cerfa avant dépôt ANTS ; l'adresse du justificatif fait foi.",
        ['adresse'],
        ['justif_domicile', 'cerfa_demande_immat'],
      );
    }

    if (isLowConfidence) {
      return makeFinding(
        'coherence_adresse_jdc_13750',
        'info',
        `L'adresse semble différer entre le justificatif de domicile et le Cerfa 13750 (${labels}), ` +
          'mais la lecture est incertaine. → Vérifiez visuellement ; si les deux adresses concordent, ignorez ce message.',
        ['adresse'],
        ['justif_domicile', 'cerfa_demande_immat'],
      );
    }
    return makeFinding(
      'coherence_adresse_jdc_13750',
      'warning',
      `L'adresse du Cerfa demande d'immatriculation ne correspond pas au justificatif de domicile (${labels}). ` +
        "→ C'est l'adresse du justificatif de domicile qui fait foi et qui sera immatriculée : corrigez le Cerfa, " +
        'ou fournissez un justificatif de domicile à la bonne adresse.',
      ['adresse'],
      ['justif_domicile', 'cerfa_demande_immat'],
    );
  },
};

// 36. Pièce d'identité du CO-TITULAIRE ACQUÉREUR : présence des deux faces.
//
// Retour François 2026-07-20 (EQ-598-TE) : « Pièce d'identité du cotitulaire
// verso manquant et non remonté ». Il a dû le réclamer à la main dans un
// commentaire externe — l'app n'avait rien détecté.
//
// D'OÙ VIENT `ctx.docs.piece_identite_cotitulaire` : deux chemins.
//  1. Zone dédiée : upload avec type IMPOSÉ `piece_identite_cotitulaire`
//     (extraction OCR complète — cas nominal).
//  2. Repli premier lot : une CNI co-titulaire déposée dans la dropzone
//     principale est classée `piece_identite` (slot générique) ; quand le
//     départage par nom l'identifie (`resolveCotitulairePiFromBatch`),
//     FormCGIAv2 l'injecte dans le contexte des règles sous cette clé —
//     sinon cette règle criait « aucune pièce déposée » pendant que la
//     checklist affichait « Rattachée au co-titulaire » (re-test EQ-598-TE,
//     2026-07-24).
//
// FAUTE D'EXTRACTION (aucun des deux chemins), la règle retombe sur le seul
// signal disponible — le NOMBRE de fichiers de la zone dédiée — et sur la
// déclaration d'un co-titulaire acquéreur sur le Cerfa 13750 : garde
// volontairement grossière qui couvre le cas réel signalé (aucune pièce, ou
// une seule face).
const pieceIdentiteCotitulaireIncomplete: CrossCheckRule = {
  id: 'piece_identite_cotitulaire_incomplete',
  description: "La pièce d'identité du co-titulaire acquéreur doit être fournie recto ET verso",
  severity: 'warning',
  check: (ctx) => {
    const cerfa = ctx.docs.cerfa_demande_immat;
    if (!cerfa) return null;

    // Un co-titulaire acquéreur est déclaré si la case dédiée est cochée sur le
    // 13750, ou si son nom y est renseigné. Sur EQ-598-TE : cotitulaire_present
    // = true (0.95) et cotitulaire_nom = « DAILLY ».
    const present = getFieldValue<boolean>(cerfa, 'cotitulaire_present') === true;
    const nom = (getFieldValue<string>(cerfa, 'cotitulaire_nom') ?? '').trim();
    if (!present && !nom) return null;

    const who = nom ? `du co-titulaire ${nom}` : 'du co-titulaire';
    const count = ctx.cotitulaireAcheteurFilesCount ?? 0;

    // Le permis format carte vaut pièce d'identité « pour tout le monde »
    // (arbitrage `permis_piece_identite`, 09/09/2026) : quand le dossier porte
    // un permis carte au nom du co-titulaire, son identité est établie et on ne
    // réclame plus une CNI qui ferait doublon. DY-565-KJ : « il y a seulement le
    // permis de BEAUSSE YOANN, mais c'est le nouveau format donc accepté en
    // carte d'identité ».
    const permisDuCotitulaire = [ctx.docs.permis_conduire, ctx.docs.permis_conduire_cotitulaire].find(
      (permis) => permis && nom && permisVautPieceIdentite(permis) && nomMatchesPi(nom, permis),
    );
    if (permisDuCotitulaire) return null;

    // ── Signal OCR (depuis le lot « OCR pièce co-titulaire ») ──────────────
    // Les pièces déposées dans la zone co-titulaire passent désormais par
    // l'extraction avec le type imposé `piece_identite_cotitulaire`. Quand une
    // extraction existe, on raisonne sur le CONTENU (quelle face a été
    // fournie) au lieu du simple comptage de fichiers.
    const pi = ctx.docs.piece_identite_cotitulaire;
    if (pi) {
      // `face_identite_presente === false` ⇒ la face fournie est le verso seul.
      // Symétrique de `piece_identite_recto_manquant` pour l'acquéreur, avec la
      // même garde de confiance : un flag peu fiable donne une relecture, pas
      // un blocage (retour Biwiz 2026-07 sur les faux « recto manquant »).
      const hasFace = getFieldValue<boolean>(pi, 'face_identite_presente');
      const faceConf = getFieldConfidence(pi, 'face_identite_presente');
      if (hasFace === false) {
        if (faceConf !== null && faceConf < 0.85) {
          return makeFinding(
            'piece_identite_cotitulaire_incomplete',
            'warning',
            `Le recto (côté photo) de la pièce d'identité ${who} n'a pas été détecté avec certitude. ` +
              "→ Vérifiez qu'il est bien joint ; s'il est déjà présent, ignorez ce message.",
            ['cotitulaire_nom'],
            ['piece_identite_cotitulaire'],
          );
        }
        return makeFinding(
          'piece_identite_cotitulaire_incomplete',
          'warning',
          `${PIECE_NON_VERIFIEE}seule la face verso de la pièce d'identité ${who} est reconnue, le recto (côté photo) n'a pas été retrouvé. ` +
            '→ Vérifiez la pièce déposée ; si le recto manque réellement, ajoutez-le.',
          ['cotitulaire_nom'],
          ['piece_identite_cotitulaire'],
        );
      }
      // Recto présent : reste à s'assurer que le verso l'accompagne. Le
      // routage (personne × face) matérialise le verso dans son slot dédié
      // `piece_identite_cotitulaire_verso` — c'est le signal fiable (retour
      // François FX-187-XA : le verso était déposé mais égaré en slot
      // hébergeur → faux « seul le recto déposé »). À défaut : recto/verso
      // dans un même PDF (`est_recto_seul = false`) ou plusieurs fichiers
      // déposés dans la zone dédiée.
      const versoPresent =
        Boolean(ctx.docs.piece_identite_cotitulaire_verso) ||
        getFieldValue<boolean>(pi, 'est_recto_seul') === false ||
        count > 1;
      if (!versoPresent) {
        // ⚠️ Avant de réclamer le verso, vérifier qu'il n'est pas DÉJÀ AU
        // DOSSIER sous un autre nom. Sur une CNI ANCIEN format, la bande MRZ
        // est imprimée au RECTO : le verso ne porte ni nom, ni n° de document
        // — seulement l'adresse et les dates de validité. Un tel verso est
        // structurellement non attribuable, et le routage le pose par défaut
        // chez le titulaire. Réclamer « le verso du co-titulaire » est alors
        // FAUX : la pièce a bien été déposée, c'est son rattachement qui est
        // incertain. Cas EA-035-SV (retour Luc 2026-07-30) : couple aux CNI
        // délivrées le même mois, verso de Mme LEGRAND classé chez M. LEFEBVRE
        // → blocage majeur alors que le dossier était complet.
        const versoTitulaire = ctx.docs.piece_identite_verso;
        const versoNonAttribuable =
          Boolean(versoTitulaire) &&
          !getFieldValue<string>(versoTitulaire!, 'nom') &&
          !getFieldValue<string>(versoTitulaire!, 'numero_document');
        if (versoNonAttribuable) {
          return makeFinding(
            'piece_identite_cotitulaire_incomplete',
            'warning',
            `Un verso de pièce d'identité a bien été déposé, mais il ne porte ni nom ni numéro ` +
              `(CNI ancien format : la MRZ est au recto) — impossible de déterminer automatiquement ` +
              `s'il s'agit du verso ${who} ou de celui du titulaire. ` +
              `→ Dans la liste des pièces, indiquez à qui appartient cette face ; ` +
              `s'il manque réellement, ajoutez le verso ${who}.`,
            ['cotitulaire_nom'],
            ['piece_identite_cotitulaire', 'piece_identite_verso'],
          );
        }
        return makeFinding(
          'piece_identite_cotitulaire_incomplete',
          'warning',
          `${PIECE_NON_VERIFIEE}seul le recto de la pièce d'identité ${who} est reconnu. ` +
            '→ Vérifiez la pièce déposée ; si le verso manque réellement, ajoutez-le.',
          ['cotitulaire_nom'],
          ['piece_identite_cotitulaire'],
        );
      }
      return null;
    }

    // Verso routé sans recto : la face photo du co-titulaire manque.
    if (ctx.docs.piece_identite_cotitulaire_verso) {
      return makeFinding(
        'piece_identite_cotitulaire_incomplete',
        'warning',
        `${PIECE_NON_VERIFIEE}seule la face verso de la pièce d'identité ${who} est reconnue, le recto (côté photo) n'a pas été retrouvé. ` +
          '→ Vérifiez la pièce déposée ; si le recto manque réellement, ajoutez-le.',
        ['cotitulaire_nom'],
        ['piece_identite_cotitulaire_verso'],
      );
    }

    if (count === 0) {
      return makeFinding(
        'piece_identite_cotitulaire_incomplete',
        'error',
        `Le Cerfa demande d'immatriculation déclare un co-titulaire, mais aucune pièce d'identité ${who} n'a été déposée. ` +
          "→ Ajoutez sa pièce d'identité (recto ET verso) dans la zone dédiée au co-titulaire : sans elle, l'ANTS rejettera la demande.",
        ['cotitulaire_nom'],
        ['cerfa_demande_immat'],
      );
    }
    if (count === 1) {
      return makeFinding(
        'piece_identite_cotitulaire_incomplete',
        'warning',
        `${PIECE_NON_VERIFIEE}un seul fichier a été déposé pour la pièce d'identité ${who} — il peut porter les deux faces. ` +
          '→ Vérifiez visuellement le recto (photo) et le verso ; si une face manque réellement, ajoutez-la.',
        ['cotitulaire_nom'],
        ['cerfa_demande_immat'],
      );
    }
    return makeFinding(
      'piece_identite_cotitulaire_incomplete',
      'info',
      `Les ${count} pièces du co-titulaire ne sont pas analysées automatiquement (dépôt hors OCR). ` +
        '→ Vérifiez visuellement que le recto ET le verso sont bien présents et lisibles.',
      ['cotitulaire_nom'],
      ['cerfa_demande_immat'],
    );
  },
};

// 35-bis. Adresse de l'acquéreur : justificatif de domicile vs certificat de
// cession.
//
// Arbitrage `jdd_prioritaire` du 09/09/2026 : « l'adresse doit toujours être
// celle du justificatif de domicile. Par contre alerte si ce n'est pas la même
// que les cerfas : le certificat de cession ou la demande d'immatriculation. »
// Le 13750 est déjà couvert par `coherence_adresse_jdc_13750` ; il manquait la
// cession. FA-762-AM : l'adresse avait été prise sur l'attestation d'assurance
// alors que le justificatif en portait une autre.
//
// La cession ne porte qu'un CODE POSTAL exploitable pour l'acquéreur
// (`acheteur_adresse_cp`) : la comparaison s'y limite, ce qui suffit à repérer
// une commune différente sans inventer une comparaison de libellés absents.
const coherenceAdresseJdcCession: CrossCheckRule = {
  id: 'coherence_adresse_jdc_cession',
  description: "Le code postal de l'acquéreur sur la cession doit correspondre au justificatif de domicile",
  severity: 'warning',
  check: (ctx) => {
    const jdc = ctx.docs.justif_domicile;
    const cession = ctx.docs.cerfa_cession;
    if (!jdc || !cession) return null;

    const cpJdc = (getFieldValue<string>(jdc, 'code_postal') ?? '').trim();
    const cpCession = (getFieldValue<string>(cession, 'acheteur_adresse_cp') ?? '').trim();
    if (!cpJdc || !cpCession || cpJdc === cpCession) return null;

    // Même garde que les autres règles d'adresse : une divergence portée par
    // une lecture douteuse est un indice de relecture, pas un constat.
    const CONFIDENCE_THRESHOLD = 0.85;
    const confJdc = getFieldConfidence(jdc, 'code_postal');
    const confCession = getFieldConfidence(cession, 'acheteur_adresse_cp');
    const lectureDouteuse =
      (confJdc !== null && confJdc < CONFIDENCE_THRESHOLD) ||
      (confCession !== null && confCession < CONFIDENCE_THRESHOLD);

    return makeFinding(
      'coherence_adresse_jdc_cession',
      lectureDouteuse ? 'info' : 'warning',
      `Le code postal de l'acquéreur diffère entre le justificatif de domicile (${cpJdc}) et le certificat de cession ` +
        `(${cpCession}). L'adresse retenue reste celle du justificatif, qui fait foi. → Vérifiez la cession : si elle porte ` +
        "une ancienne adresse, faites-la corriger ; si c'est le justificatif qui n'est pas à la bonne adresse, fournissez-en un autre.",
      undefined,
      ['justif_domicile', 'cerfa_cession'],
    );
  },
};

// 35-ter. Le titulaire déclaré sur la demande d'immatriculation doit être
// l'ACQUÉREUR de la cession.
//
// GB-976-SV (retour du 26/08) : « l'agence a renseigné la demande
// d'immatriculation avec les informations de l'acheteur qui sont les mêmes que
// le vendeur ». Le 13750 portait MERAZGA (le vendeur, titulaire de la carte
// grise) alors que la cession vend à LEBARBÉ. Le dossier partait immatriculer
// le véhicule au nom de celui qui le vendait, et rien ne le signalait : aucune
// règle ne croisait ces deux champs.
//
// Alerte et non blocage, comme les autres contrôles d'identité arbitrés le
// 09/09 : le relecteur tranche, et un nom d'usage ou une raison sociale lue de
// travers ne doit pas immobiliser un dossier.
const titulaire13750EstLeVendeur: CrossCheckRule = {
  id: 'titulaire_13750_est_le_vendeur',
  description: "Le titulaire de la demande d'immatriculation et le mandant doivent être l'acquéreur, pas le vendeur",
  severity: 'warning',
  check: (ctx) => {
    const cerfa = ctx.docs.cerfa_demande_immat;
    const cession = ctx.docs.cerfa_cession;
    if (!cession) return null;

    const acheteurCession = getFieldValue<string>(cession, 'acheteur_nom');
    const vendeurCession = getCessionVendeurNom(ctx);
    if (!acheteurCession || !vendeurCession) return null;

    // Vente entre deux personnes du même nom (succession, famille) : le
    // croisement ne prouve rien, on se tait plutôt que d'accuser à tort.
    if (namesMatch(acheteurCession, vendeurCession)) return null;

    // Le mandat part avec le 13750 : sur GB-976-SV les DEUX étaient au nom du
    // vendeur (« LE MANDAT CERFA 13757*03 […] renseigné par MEGAZGA FEHDE et
    // non LEBARBÉ EVAN »). Les deux Cerfas sont donc vérifiés ensemble, et
    // signalés ensemble : c'est la même erreur de remplissage, elle se corrige
    // d'un seul geste.
    const pieces: Array<{ doc: UploadedDocResult | undefined; field: string; slot: CgDocType; label: string }> = [
      { doc: cerfa, field: 'titulaire_nom', slot: 'cerfa_demande_immat', label: "la demande d'immatriculation" },
      { doc: ctx.docs.mandat, field: 'mandant_nom', slot: 'mandat', label: 'le mandat' },
    ];

    const fautives = pieces.filter(({ doc, field }) => {
      const nomLu = getFieldValue<string>(doc, field);
      if (!nomLu) return false;
      return !namesMatch(nomLu, acheteurCession) && namesMatch(nomLu, vendeurCession);
    });
    if (fautives.length === 0) return null;

    const libelles = fautives.map((p) => p.label).join(' et ');
    const accord = fautives.length > 1 ? 'sont remplis' : 'est rempli';
    return makeFinding(
      'titulaire_13750_est_le_vendeur',
      'warning',
      `${libelles.charAt(0).toUpperCase()}${libelles.slice(1)} ${accord} au nom de ${vendeurCession}, qui est le VENDEUR de la cession — l'acquéreur y est ${acheteurCession}. ` +
        "Le véhicule serait immatriculé au nom de celui qui le vend. → Faites refaire ces pièces au nom de l'acquéreur.",
      ['titulaire_nom'],
      fautives.map((p) => p.slot),
    );
  },
};

// 36-bis. Pièce d'identité qui ne correspond à PERSONNE du dossier.
//
// CY-517-VW (retour du 22/07) : la CNI de Mme Carolina FATATI était rattachée à
// l'acquéreur BERNIER Ronald — ses date et lieu de naissance sont partis dans le
// formulaire. Elle n'est ni acquéreur, ni co-titulaire, ni hébergeur : c'est une
// pièce déposée par erreur. Le routage l'isole désormais sans polluer
// l'acquéreur, mais rien ne disait à l'agence de la retirer.
//
// Arbitrage `piece_tiers_classement` du 09/09/2026 : « section pièce étrangère
// au dossier, à retirer », bien visible pour l'agence. Point d'attention et non
// blocage : une pièce en trop n'empêche pas la soumission, elle appelle un
// ménage — et une lecture de nom approximative ne doit pas coincer un dossier.
const PI_SLOTS_RATTACHABLES: readonly CgDocType[] = [
  'piece_identite',
  'piece_identite_cotitulaire',
  'piece_identite_hebergeur',
];

const pieceIdentiteEtrangereAuDossier: CrossCheckRule = {
  id: 'piece_identite_etrangere_au_dossier',
  description: "Une pièce d'identité déposée ne correspond à aucune personne du dossier",
  severity: 'warning',
  check: (ctx) => {
    const cession = ctx.docs.cerfa_cession;
    if (!cession) return null; // Sans cession, aucun nom de référence : on se tait.

    const cerfa = ctx.docs.cerfa_demande_immat;
    const attestation = ctx.docs.attestation_hebergement;
    const nomsDuDossier = [
      getFieldValue<string>(cession, 'acheteur_nom'),
      getFieldValue<string>(cession, 'acheteur_cotitulaire_nom'),
      getCessionVendeurNom(ctx),
      getFieldValue<string>(ctx.docs.carte_grise, 'titulaire_nom'),
      getFieldValue<string>(ctx.docs.carte_grise, 'titulaire_nom_usage'),
      getFieldValue<string>(cerfa, 'titulaire_nom'),
      getFieldValue<string>(cerfa, 'cotitulaire_nom'),
      getFieldValue<string>(attestation, 'hebergeur_nom'),
      getFieldValue<string>(attestation, 'heberge_nom'),
    ].filter((nom): nom is string => Boolean(nom && nom.trim()));

    // Aucune référence exploitable (Cerfas illisibles) → aucune conclusion.
    if (nomsDuDossier.length === 0) return null;

    const slotEtranger = PI_SLOTS_RATTACHABLES.find((slot) => {
      const pi = ctx.docs[slot];
      if (!pi) return false;
      // Pièce de l'HÉBERGEUR sans attestation d'hébergement au dossier : son
      // nom n'est écrit nulle part, il n'y a donc aucune référence à laquelle
      // la comparer. Se taire, plutôt que de traiter tout hébergeur comme un
      // tiers tant que son attestation n'est pas analysée.
      if (slot === 'piece_identite_hebergeur' && !attestation) return false;
      const piNom = getFieldValue<string>(pi, 'nom');
      // Nom non lu : rien à comparer, on ne conclut pas (donnée mal lue ≠ tiers).
      if (!piNom || !piNom.trim()) return false;
      return !nomsDuDossier.some((nom) => nomMatchesPi(nom, pi));
    });
    if (!slotEtranger) return null;

    const pi = ctx.docs[slotEtranger];
    const identite = [getFieldValue<string>(pi, 'prenom'), getFieldValue<string>(pi, 'nom')]
      .filter(Boolean)
      .join(' ')
      .trim();

    return makeFinding(
      'piece_identite_etrangere_au_dossier',
      'warning',
      `Pièce étrangère au dossier, à retirer : la pièce d'identité de ${identite} ne correspond à aucune personne du dossier ` +
        "(ni acquéreur, ni co-titulaire, ni vendeur, ni hébergeur). → Retirez-la et déposez, si elle manque, la pièce de la bonne personne — " +
        "une pièce en trop finit par alimenter les champs d'identité du mauvais titulaire.",
      undefined,
      [slotEtranger],
    );
  },
};

// 37. Pièce qui FAIT FOI absente du dossier constitué.
//
// Le moteur croise des valeurs ENTRE pièces : quand la carte grise manque, les
// trente-six règles qui la lisent retournent toutes `null`, et le dossier
// ressort sans la moindre alerte. Le silence du moteur se lit alors comme un
// dossier propre — c'est le seul FAUX NÉGATIF du lot de retours : DW-841-DG
// (2026-08-13), « tu dis dossier conforme alors qu'il manque la carte grise ».
// L'audit Opus, lui, avait bien rendu `CG-ABSENT` en blocage ; c'est le
// snapshot regex vide, figé à la soumission, qui l'a masqué en review.
//
// Garde de constitution : la règle exige la présence d'un Cerfa (cession ou
// demande d'immatriculation). Sans elle, elle crierait dès le premier fichier
// déposé, alors que la checklist de dépôt annonce déjà ce qui reste à fournir.
// Un dossier soumis porte toujours au moins un de ces deux Cerfas.
const pieceMaitresseAbsente: CrossCheckRule = {
  id: 'piece_maitresse_absente',
  description: 'Une pièce qui fait foi (carte grise, certificat de cession) manque au dossier constitué',
  severity: 'error',
  check: (ctx) => {
    const dossierConstitue = Boolean(ctx.docs.cerfa_cession || ctx.docs.cerfa_demande_immat);
    if (!dossierConstitue) return null;

    if (!ctx.docs.carte_grise) {
      return makeFinding(
        'piece_maitresse_absente',
        'error',
        "Aucune carte grise n'a été reconnue dans le dossier. C'est la pièce qui fait foi pour le véhicule " +
          "(immatriculation, VIN, titulaire C.1, n° de formule, mention « Vendu le ») : sans elle, aucun contrôle " +
          "de cohérence n'est possible et la soumission ANTS est refusée. → Déposez la carte grise barrée et signée ; " +
          'si elle a bien été déposée, vérifiez qu\'elle est nette et entièrement visible.',
        undefined,
        ['carte_grise'],
      );
    }

    if (!ctx.docs.cerfa_cession) {
      return makeFinding(
        'piece_maitresse_absente',
        'error',
        "Aucun certificat de cession (Cerfa 15776*02) n'a été reconnu dans le dossier. C'est la seule pièce qui porte " +
          'la transaction (date et heure de vente, kilométrage, signatures) : sans elle, la soumission ANTS est refusée. ' +
          "→ Déposez le certificat de cession signé par le vendeur et l'acquéreur ; s'il a bien été déposé, vérifiez " +
          "qu'il est net et entièrement visible.",
        undefined,
        ['cerfa_cession'],
      );
    }

    return null;
  },
};

/**
 * Liste exhaustive des règles — ordre d'exécution = ordre d'affichage dans la bannière.
 */
export const ALL_CROSS_CHECK_RULES: CrossCheckRule[] = [
  pieceMaitresseAbsente,
  pieceIdentiteEtrangereAuDossier,
  coherenceAdresseJdcCession,
  titulaire13750EstLeVendeur,
  coherenceAdresseJdc13750,
  pieceIdentiteCotitulaireIncomplete,
  incoherenceDocument,
  cessionVendeurSirenAuLieuDeSiret,
  numeroFormuleCouponDetache,
  coherenceImmatUserVsPieces,
  pieceAutreVehicule,
  regexImmatSiv,
  vinFormat,
  vinCheckDigit,
  coherenceImmatCgCession,
  coherenceVinCgCession,
  coherenceVendeurCgCession,
  coherenceAcquereurCessionVsPi,
  ctObligatoireSiMec4Ans,
  ctValidite,
  assuranceNonDevis,
  assuranceMatchVehicule,
  justifDomicileRecent,
  signatureCessionCotitulaire,
  mandatSkipcarValide,
  pasLocationNiCreditBail,
  mandatOperationTypeVide,
  mandatCaseAssuranceNonCochee,
  kbisEntrepriseRadiee,
  parcoursARequis,
  recepisseDaManquantParcoursA,
  chronologieRecepisseDa,
  cessionIllisible,
  numeroFormuleManquantCession,
  numeroFormuleManquant13750,
  casesCertificationAcquereurManquantes,
  coherenceImmatSkippedLowConfidence,
  misivSkippedLowConfidence,
  vinMisivMismatch,
  mecMisivMismatch,
  dateCerfaCoherente,
  ancienneCgPapierRose,
  cgEtrangere,
  personneMoraleSiren,
  detectionMicroEntrepreneur,
  permisFaceIdentiteManquante,
  pieceIdentiteFaceManquante,
  pieceIdentiteMultipleMemeFichier,
  coherenceDateCessionCgMention,
  cerfaCaseIncorrecte,
  cerfa13750CaseOperationManquante,
  pieceIdentiteExpiree,
];

/**
 * Helper : exécute toutes les règles du plan sur un contexte donné.
 */
export const runAllCrossChecks = (context: CrossCheckContext) => runCrossChecks(ALL_CROSS_CHECK_RULES, context);

/**
 * Builder de contexte depuis la map `uploadedDocs` du store.
 */
export const buildContextFromDocs = (docs: CrossCheckContext['docs'], now: Date = new Date()): CrossCheckContext => ({
  docs,
  now,
});
